<template>
  <div class="cresiteiq2-container h-full flex">
    <!-- Left Sidebar -->
    <div class="sidebar w-80 bg-white border-r border-gray-200 flex flex-col">
      <!-- Sidebar Header -->
      <div class="sidebar-header bg-gray-50 border-b border-gray-200 p-4">
        <h2 class="text-lg font-semibold text-gray-800">CRESiteIQ™ GIS</h2>
      </div>
      
      <!-- Sidebar Content -->
      <div class="sidebar-content flex-1 overflow-y-auto p-4 space-y-6">
        <!-- Location Search Component -->
        <LocationSearch 
          @location-selected="onLocationSelected"
        />
        
        <!-- Risk Comparison Section -->
        <div v-if="shouldShowComparisonSection" class="risk-comparison-section">
          <div class="mb-3">
            <h3 class="text-md font-semibold text-gray-700">Comparison</h3>
          </div>
          
          <div class="space-y-3">
            <!-- Risk Comparison Toggle -->
            <label class="flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                v-model="comparisonEnabled"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              >
              <span class="ml-2 text-sm text-gray-700">Enable Comparison</span>
            </label>
            
            <!-- Comparison Scope Selector (shown when toggle is enabled) -->
            <div v-if="comparisonEnabled" class="ml-6 space-y-2 pl-4 border-l-2 border-blue-200">
              <label class="text-sm font-medium text-gray-700 block mb-2">Comparison Scope:</label>
              
              <label class="flex items-center cursor-pointer">
                <input 
                  type="radio" 
                  v-model="comparisonScope"
                  value="state"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                >
                <span class="ml-2 text-sm text-gray-700">State Level</span>
              </label>
              
              <label class="flex items-center cursor-pointer">
                <input 
                  type="radio" 
                  v-model="comparisonScope"
                  value="county"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                >
                <span class="ml-2 text-sm text-gray-700">County Level</span>
              </label>
            </div>
          </div>
        </div>
        
        <!-- AGS Data Section -->
        <Cresiteiq2AGSFilter
          :current-zoom="currentZoom"
          @open-filter-modal="showAgsFilterModal = true"
          @variables-updated="onAgsVariablesUpdated"
          @opacity-changed="onAgsOpacityChanged"
          @scale-direction-changed="onScaleDirectionChanged"
          @weights-changed="onWeightsChanged"
        />
        
        <!-- Crime Data Section -->
        <div v-if="showCrimeFilters" class="crime-data-section mt-6">
          <div class="mb-3">
            <h3 class="text-md font-semibold text-gray-700">Crime Data</h3>
          </div>
          
          <!-- Crime Filters -->
          <div class="space-y-4">
            
            <div v-if="showCrimeFilters" class="space-y-3">
              <!-- Selected Filters Display -->
              <div v-if="selectedCrimeTypes.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div class="flex items-center justify-between mb-2">
                  <h4 class="text-sm font-medium text-blue-900">Selected Filters</h4>
                  <button
                    @click="clearAllCrimeFilters"
                    class="text-xs text-blue-600 hover:text-blue-800 underline transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <div class="flex flex-wrap gap-1">
                  <span 
                    v-for="crimeType in selectedCrimeTypes.slice(0, 3)" 
                    :key="crimeType"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 group"
                  >
                    {{ getCrimeTypeLabel(crimeType) }}
                    <button
                      @click="removeCrimeFilter(crimeType)"
                      class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 transition-colors"
                      :title="`Remove ${getCrimeTypeLabel(crimeType)} filter`"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </span>
                  <span 
                    v-if="selectedCrimeTypes.length > 3"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    +{{ selectedCrimeTypes.length - 3 }} more
                  </span>
                </div>
              </div>
              
              <!-- Add Filter(s) Button -->
              <div v-else class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                <p class="text-sm text-gray-500 mb-3">No crime filters selected</p>
              </div>
              
              <!-- Filter Button -->
              <button
                @click="showCrimeFilterModal = true"
                class="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                </svg>
                {{ selectedCrimeTypes.length > 0 ? 'Edit Filters' : 'Add Filter(s)' }}
              </button>
              
              <!-- Crime Data Opacity Slider -->
              <div v-if="selectedCrimeTypes.length > 0" class="mt-4 pt-4 border-t border-gray-200">
                <div class="mb-3">
                  <div class="flex items-center justify-between">
                    <label class="text-sm font-medium text-gray-700">Crime Data Opacity</label>
                    <span class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{{ Math.round(crimeDataOpacity * 100) }}%</span>
                  </div>
                </div>
                
                <div class="relative">
                  <!-- Opacity Slider -->
                  <input
                    type="range"
                    v-model="crimeDataOpacity"
                    @input="onOpacityChange"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer opacity-slider"
                  >
                  
                  <!-- Slider Labels -->
                  <div class="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
                
                <!-- Quick Opacity Buttons -->
                <div class="flex gap-1 mt-3">
                  <button
                    v-for="preset in opacityPresets"
                    :key="preset.value"
                    @click="setOpacityPreset(preset.value)"
                    class="flex-1 px-2 py-1 text-xs rounded transition-colors"
                    :class="Math.abs(crimeDataOpacity - preset.value) < 0.05 
                      ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                  >
                    {{ preset.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Main Content Area -->
    <div class="main-content flex-1 flex flex-col">
      <!-- Google Maps Container -->
      <div class="map-container flex-1 relative">
        <div id="cresiteiq2-map" class="w-full h-full"></div>

        <!-- Zoom Level Selector (Google Maps Style) -->
        <div class="absolute top-28 left-4 z-20">
          <div class=" overflow-hidden">
            <!-- Zoom Controls -->
            <div class="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200">
              <button 
                @click="zoomIn"
                class="w-10 h-10 flex items-center justify-center border-b border-gray-200 hover:bg-gray-50 transition-colors"
                :disabled="currentZoom >= zoomSlider.max"
              >
                <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
              </button>
              
              <!-- Zoom Level Indicator -->
              <div 
                class="w-10 h-20 relative bg-gray-50 mb-1 cursor-pointer"
                @mouseenter="showZoomLevelPopup = true"
                @mouseleave="showZoomLevelPopup = false"
              >
                <!-- Zoom Track -->
                <div class="absolute left-1/2 top-2 bottom-2 w-1 bg-gray-300 transform -translate-x-1/2 rounded-full"></div>
                
                <!-- Zoom Thumb -->
                <div 
                  class="absolute left-1/2 w-3 h-3 bg-blue-500 border-2 border-white rounded-full transform -translate-x-1/2 shadow-sm"
                  :style="{ top: zoomThumbPosition + 'px' }"
                ></div>
                
                <!-- Current Zoom Display -->
                <div class="absolute inset-0 flex items-center justify-center">
                  <span class="text-xs font-semibold text-gray-700 bg-white px-1 rounded shadow-sm">{{ currentZoom }}</span>
                </div>
              </div>
              
              <button 
                @click="zoomOut"
                class="w-10 h-10 flex items-center justify-center border-t border-gray-200 hover:bg-gray-50 transition-colors"
                :disabled="currentZoom <= zoomSlider.min"
              >
                <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 12H6"/>
                </svg>
              </button>
            </div>
            
            <!-- Additional Controls -->
            <div class="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 mt-2">
              <!-- Home/Reset Button -->
              <button 
                @click="resetToHome"
                class="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 hover:bg-gray-50 transition-colors rounded-t-lg"
                title="Reset to home view"
              >
                <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>
              </button>
              
              <!-- Current Location Button -->
              <button 
                @click="zoomToCurrentLocation"
                class="w-10 h-10 flex items-center justify-center bg-white border-l border-r border-b border-gray-200 hover:bg-gray-50 transition-colors rounded-b-lg"
                :disabled="gettingLocation"
                title="Zoom to current location"
              >
                <svg v-if="!gettingLocation" class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <svg v-else class="w-4 h-4 text-gray-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </button>
            </div>

            <div class="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 mt-2">
               <!-- Crime Legend Toggle Button (Left Side) -->
        <button 
          v-if="currentZoom >= 10 && selectedCrimeTypes.length > 0 && crimeColorScale.length > 0 && !showCrimeLegend"
          @click="showCrimeLegend = true"
          class="bg-white rounded-lg shadow-lg border border-gray-200 p-2 hover:bg-gray-50 transition-colors"
          title="Show crime risk legend"
        >
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"/>
          </svg>
        </button>
        
        <!-- AGS Legend Toggle Button (Left Side) -->
        <button 
          v-if="selectedAgsVariables.length > 0 && agsColorScale.length > 0 && !showAgsLegend"
          @click="showAgsLegend = true"
          class="bg-white rounded-lg shadow-lg border border-gray-200 p-2 hover:bg-gray-50 transition-colors mt-2"
          title="Show AGS data legend"
        >
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
        </button>
            </div>
          </div>
          
          <!-- Suggested Zoom Level Popup -->
          <transition name="fade">
            <div
              v-if="showZoomLevelPopup"
              class="absolute left-0 top-0 ml-12 bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[200px]"
            >
              <h3 class="text-sm font-semibold text-gray-800 mb-2 pb-2 border-b border-gray-200">Zoom level</h3>
              
              <div class="space-y-1">
                <div 
                  v-for="layer in zoomLevelLayers" 
                  :key="layer.name"
                  class="flex items-center py-1 px-2 rounded-md transition-colors"
                  :class="layer.active ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'"
                >                 
                  <!-- Layer Name and Zoom Range -->
                  <div class="flex-1 flex items-center justify-between">
                    <span 
                      class="text-sm"
                      :class="layer.active ? 'font-bold text-blue-900' : 'text-gray-600'"
                    >
                      {{ layer.name }}
                    </span>
                    
                    <span 
                      class="text-xs font-medium"
                      :class="layer.active ? 'text-blue-700' : 'text-gray-500'"
                    >
                      {{ layer.zoomRange }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
         <!-- Crime Data Legend (Left Side) -->
        <div 
          v-if="showCrimeLegend" 
          class="absolute bottom-5 left-4 z-30 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-xs"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold text-gray-800">Risk Level</h3>
            <button 
              @click="hideCrimeLegend"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Hide legend"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div v-if="comparisonEnabled" class="mb-2">
            <p class="text-xs text-gray-600 italic">
              Compared to {{ comparisonScope === 'state' ? 'State' : 'County' }} Average
            </p>
          </div>
          
          <div class="space-y-2">
            <div 
              v-for="(item, index) in crimeColorScale" 
              :key="index"
              class="flex items-center space-x-3"
            >
              <!-- Color Swatch -->
              <div 
                class="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                :style="{ backgroundColor: item.color }"
              ></div>
              <!-- Risk Label -->
              <span class="text-xs text-gray-700 flex-1">{{ item.label }}</span>
            </div>
          </div>
          
          <div v-if="!comparisonEnabled" class="mt-3 pt-2 border-t border-gray-200">
            <p class="text-xs text-gray-500 italic">
              Risk Level (Viewport)
            </p>
          </div>
        </div>

        <!-- AGS Data Legend (Left Side) -->
        <div 
          v-if="showAgsLegend && selectedAgsVariables.length > 0" 
          class="absolute bottom-5 left-4 z-30 bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-xs"
          :style="{ marginBottom: showCrimeLegend ? '200px' : '20px' }"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold text-gray-800">AGS Data</h3>
            <button 
              @click="hideAgsLegend"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Hide AGS legend"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div class="mb-2">
            <p class="text-xs text-gray-600 italic">
              {{ selectedAgsVariables.length }} variable{{ selectedAgsVariables.length !== 1 ? 's' : '' }} selected
            </p>
          </div>
          
          <div class="space-y-2">
            <div 
              v-for="(item, index) in agsColorScale" 
              :key="index"
              class="flex items-center space-x-3"
            >
              <!-- Color Swatch -->
              <div 
                class="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                :style="{ backgroundColor: item.color }"
              ></div>
              <!-- Value Label -->
              <span class="text-xs text-gray-700 flex-1">{{ item.label }}</span>
            </div>
          </div>
          
          <div class="mt-3 pt-2 border-t border-gray-200">
            <p class="text-xs text-gray-500 italic">
              AGS Data Values
            </p>
          </div>
        </div>
     
        </div>
        

        <button 
          v-if="currentZoom < 4 || (currentZoom >= 6 && currentZoom < 8)" 
          type="button" 
          class="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full text-sm font-bold py-2 px-4 border shadow-md max-w-fit z-50 bg-white text-gray-500 border-gray-400 animate-pulse" 
          disabled="disabled"
        >
          {{ currentZoom < 4 ? 'Zoom in to level 4+ to see geographic data' : 'County data not available - zoom to level 8+ for ZIP codes' }}
        </button>
        
        <!-- Hover Popover for Crime Data -->
        <div 
          v-if="showHoverPopover && currentZoom >= 10 && selectedCrimeTypes.length > 0" 
          class="absolute pointer-events-none"
          :style="{ left: hoverPopoverData.position.x + 'px', top: hoverPopoverData.position.y + 'px', zIndex: hoverPopupZIndex }"
        >
          <div class="bg-white rounded-lg shadow-xl border border-gray-300 p-3 max-w-xs min-w-48 hover-popover-content">
            <div class="text-sm">
              <div class="font-semibold text-gray-800 mb-2 text-xs">
                {{ getUnitTypeDisplayName(hoverPopoverData.unitType) }}: 
                <span class="font-mono text-blue-600">{{ hoverPopoverData.unitCode }}</span>
              </div>
              
              <!-- Risk Level Information (when comparison mode is enabled) -->
              <div v-if="hoverPopoverData.riskLevel && comparisonEnabled" class="mb-2 pb-2 border-b border-gray-200">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs text-gray-600">Risk Level:</span>
                  <span 
                    class="text-xs font-semibold px-2 py-0.5 rounded"
                    :class="getRiskLevelBadgeClass(hoverPopoverData.riskLevel)"
                  >
                    {{ formatRiskLevel(hoverPopoverData.riskLevel) }}
                  </span>
                </div>
                
                <div v-if="hoverPopoverData.riskPercentile !== null && hoverPopoverData.riskPercentile !== undefined" class="flex justify-between text-xs">
                  <span class="text-gray-600">Percentile:</span>
                  <span class="font-medium text-gray-800">{{ Math.round(hoverPopoverData.riskPercentile) }}th</span>
                </div>
                
                <div v-if="hoverPopoverData.perCapitaRate !== null && hoverPopoverData.perCapitaRate !== undefined" class="flex justify-between text-xs">
                  <span class="text-gray-600">Per Capita Rate:</span>
                  <span class="font-medium text-gray-800">{{ hoverPopoverData.perCapitaRate.toFixed(2) }}/1000</span>
                </div>
                
                <!-- Comparison Reference -->
                <div v-if="hoverPopoverData.comparisonReference" class="mt-1 pt-1 border-t border-gray-100">
                  <div class="text-xs text-gray-500 italic">
                    vs. {{ hoverPopoverData.comparisonReference.name || hoverPopoverData.comparisonReference.code }}
                  </div>
                </div>
              </div>
              
              <!-- Crime Statistics -->
              <div v-if="hoverPopoverData.crimeData && Object.keys(hoverPopoverData.crimeData).length > 0" class="space-y-1">
                <div 
                  v-for="crimeType in selectedCrimeTypes.slice(0, 4)" 
                  :key="crimeType"
                  class="flex justify-between text-xs"
                >
                  <span class="text-gray-600 truncate pr-2">{{ getCrimeTypeLabel(crimeType) }}:</span>
                  <span class="font-medium text-gray-800 font-mono">
                    {{ hoverPopoverData.crimeData[crimeType] || 'N/A' }}
                  </span>
                </div>
                <div v-if="selectedCrimeTypes.length > 4" class="text-xs text-gray-500 italic pt-1 border-t border-gray-100">
                  +{{ selectedCrimeTypes.length - 4 }} more (click for details)
                </div>
              </div>
              <div v-else class="text-xs text-gray-500 italic">
                No crime data available
              </div>
            </div>
          </div>
        </div>

        <!-- Hover Popover for AGS Data and Basic Geographic Info -->
        <div 
          v-if="showAgsHoverPopover" 
          class="absolute pointer-events-none"
          :style="{ left: agsHoverPopoverData.position.x + 'px', top: agsHoverPopoverData.position.y + 'px', zIndex: hoverPopupZIndex }"
        >
          <div class="bg-white rounded-lg shadow-xl border p-3 max-w-xs min-w-48 hover-popover-content"
               :class="selectedAgsVariables.length > 0 ? 'border-blue-300' : 'border-gray-300'">
            <div class="text-sm">
              <div class="font-semibold text-gray-800 mb-2 text-xs">
                {{ getUnitTypeDisplayName(agsHoverPopoverData.unitType) }}: 
                <span class="font-mono text-blue-600">{{ agsHoverPopoverData.unitName || agsHoverPopoverData.unitCode }}</span>
              </div>
              
              <!-- AGS Data Header (only show if AGS variables are selected) -->
              <div v-if="selectedAgsVariables.length > 0" class="mb-2 pb-2 border-b border-teal-200">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-medium text-teal-700">AGS Data</span>
                  <span class="text-xs text-gray-500">{{ selectedAgsVariables.length }} variable{{ selectedAgsVariables.length !== 1 ? 's' : '' }}</span>
                </div>
              </div>
              
              <!-- AGS Variables (only show if AGS variables are selected) -->
              <div v-if="selectedAgsVariables.length > 0">
                <div v-if="agsHoverPopoverData.agsData && Object.keys(agsHoverPopoverData.agsData).length > 0" class="space-y-1">
                  <div 
                    v-for="variable in agsHoverPopoverData.selectedVariables" 
                    :key="variable.path"
                    class="flex justify-between text-xs"
                  >
                    <span class="text-gray-600 truncate pr-2">{{ variable.label }}:</span>
                    <span class="font-medium text-gray-800 font-mono">
                      {{ formatAgsValue(null, variable) }}
                    </span>
                  </div>
                  <div v-if="selectedAgsVariables.length > 4" class="text-xs text-gray-500 italic pt-1 border-t border-gray-100">
                    +{{ selectedAgsVariables.length - 4 }} more (click for details)
                  </div>
                </div>
                <div v-else class="text-xs text-gray-500 italic">
                  No AGS data available
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Crime Data Popup -->
        <!-- Crime Data Popup -->
        <div v-if="showPopup && popupData.crimeData" class="absolute top-4 right-4 z-30 max-w-sm">
          <div class="relative">
            <button 
              @click="closePopup"
              class="absolute -top-2 -right-2 z-40 bg-white rounded-full p-1 shadow-md hover:bg-gray-50 transition-colors"
              aria-label="Close popup"
            >
              <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <CrimeDataPopup
              :unit-code="popupData.unitCode"
              :unit-type="popupData.unitType"
              :unit-name="popupData.unitName"
              :state-code="popupData.stateCode"
              :county-code="popupData.countyCode"
              :tract-code="popupData.tractCode"
              :block-group-code="popupData.blockGroupCode"
              :population-data="popupData.populationData"
              :crime-data="popupData.crimeData"
              :selected-crime-types="selectedCrimeTypes"
              :available-crime-types="availableCrimeTypes"
              :combined-value="popupData.combinedValue"
              :intensity-percent="popupData.intensityPercent"
              :crime-intensity-details="popupData.crimeIntensityDetails"
              :polygon-color="popupData.polygonColor"
              :risk-level="popupData.riskLevel"
              :normalized-score="popupData.normalizedScore"
              :crime-index="popupData.crimeIndex"
              :risk-percentile="popupData.riskPercentile"
              :per-capita-rate="popupData.perCapitaRate"
              :comparison-mode="comparisonEnabled ? comparisonScope : 'viewport'"
              :comparison-reference="popupData.comparisonReference"
              :data-year="2025"
              :show="true"
            />
          </div>
        </div>

        <!-- AGS Data Popup -->
        <div v-if="showAgsPopup && popupData.agsData" class="absolute top-4 right-4 z-30 max-w-sm">
          <div class="relative">
            <button 
              @click="closeAgsPopup"
              class="absolute -top-2 -right-2 z-40 bg-white rounded-full p-1 shadow-md hover:bg-gray-50 transition-colors"
              aria-label="Close AGS popup"
            >
              <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <AgsDataPopup
              :unit-code="popupData.unitCode"
              :unit-type="popupData.unitType"
              :state-code="popupData.stateCode"
              :county-code="popupData.countyCode"
              :tract-code="popupData.tractCode"
              :block-group-code="popupData.blockGroupCode"
              :ags-data="popupData.agsData"
              :selected-ags-variables="selectedAgsVariables"
              :ags-intensity-level="popupData.agsIntensityLevel"
              :ags-risk-level="popupData.agsRiskLevel"
              :ags-value="popupData.agsValue"
              :ags-percentage="popupData.agsPercentage"
              :ags-comparison-reference="popupData.agsComparisonReference"
              :comparison-mode="comparisonEnabled ? comparisonScope : 'viewport'"
              :comparison-reference="popupData.comparisonReference"
              :data-year="2025"
            />
          </div>
        </div>

        <!-- Fallback Warning Banner -->
        <div 
          v-if="comparisonFallbackActive && comparisonEnabled" 
          class="absolute top-4 left-1/2 transform -translate-x-1/2 z-40 max-w-md"
        >
          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-lg shadow-lg">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="ml-3 flex-1">
                <p class="text-sm text-yellow-800">
                  Using viewport-based risk levels due to temporary data unavailability.
                </p>
              </div>
              <button 
                @click="comparisonFallbackActive = false"
                class="ml-3 flex-shrink-0 text-yellow-400 hover:text-yellow-600"
              >
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Loading overlay -->
        <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div class="text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-600">Loading map data...</p>
          </div>
        </div>
        
        <!-- AGS Loading overlay -->
        <div v-if="agsLoading && !loading" class="absolute top-4 right-4 z-30 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-xs">
          <div class="flex items-center space-x-3">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
            <div class="text-sm">
              <p class="font-medium text-gray-800">{{ agsLoadingMessage }}</p>
              <p class="text-xs text-gray-600 mt-1">{{ selectedAgsVariables.length }} variable{{ selectedAgsVariables.length !== 1 ? 's' : '' }} selected</p>
            </div>
          </div>
        </div>
        
        <!-- AGS Error notification -->
        <div v-if="agsError && !loading && !agsLoading" class="absolute top-4 right-4 z-30 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 max-w-sm">
          <div class="flex items-start space-x-3">
            <svg class="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="flex-1">
              <h4 class="text-sm font-medium text-red-800">AGS Data Error</h4>
              <p class="text-xs text-red-600 mt-1">{{ agsError }}</p>
              <div class="mt-2 flex space-x-2">
                <button 
                  @click="retryAgsData"
                  :disabled="agsRetryCount >= maxAgsRetries"
                  class="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ agsRetryCount >= maxAgsRetries ? 'Max retries reached' : 'Retry' }}
                </button>
                <button 
                  @click="dismissAgsError"
                  class="text-xs text-red-600 hover:text-red-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Crime Filter Modal -->
        <CrimeFilterModal
          :show="showCrimeFilterModal"
          :selected-crime-types="selectedCrimeTypes"
          :available-crime-types="availableCrimeTypes"
          @close="showCrimeFilterModal = false"
          @save="onCrimeFiltersUpdated"
        />
        
        <!-- AGS Filter Modal -->
        <AGSFilterDialog
          :show="showAgsFilterModal"
          :selected-variables="selectedAgsVariables"
          :current-zoom="currentZoom"
          @close="showAgsFilterModal = false"
          @save="onAgsVariablesUpdated"
        />
      </div>
    </div>
</template>

<script>
import cresiteiq2Api, { ZOOM_THRESHOLDS } from '../services/cresiteiq2Api.js';
import CrimeDataPopup from '../components/CrimeDataPopup.vue';
import AgsDataPopup from '../components/AgsDataPopup.vue';
import CrimeFilterModal from '../components/CrimeFilterModal.vue';
import AGSFilterDialog from '../components/AGSFilterDialog.vue';
import Cresiteiq2AGSFilter from '../components/Cresiteiq2AGSFilter.vue';
import LocationSearch from '../components/LocationSearch.vue';
import { agsStore } from '../../../composables/useAGSStore.js';

export default {
  name: 'Cresiteiq2MapPage',
  
  components: {
    CrimeDataPopup,
    AgsDataPopup,
    CrimeFilterModal,
    AGSFilterDialog,
    Cresiteiq2AGSFilter,
    LocationSearch
  },
  
  data() {
    return {
      map: null,
      infoWindow: null,
      activeLayer: 'States',
      loading: false,
      
      // AGS Store instance
      agsStore: null,
      currentZoom: 4,
      currentBounds: null,
      polygonLayers: [],
      showLayerLegendOverlay: false,
      temporaryMarker: null,
      layerColorLegend: [
        { id: 'Block Groups', label: 'Block Groups', zoomRange: 'Zoom 13-14', fill: '#3B82F6', border: '#1D4ED8', activeClass: 'bg-blue-50 border border-blue-200' },
        { id: 'Blocks', label: 'Blocks', zoomRange: 'Zoom >= 15', fill: '#3B82F6', border: '#1D4ED8', activeClass: 'bg-blue-50 border border-blue-200' }
      ],
      zoomSliderValue: 4,
      zoomSlider: { min: 4, max: 15, step: 1 },
      overlayHoverTimeout: null,
      currentCrimeRange: { min: 0, max: 200 },
      
      // New zoom control properties
      showZoomLevelPopup: false,
      gettingLocation: false,
      
      // Initial map settings for home reset
      initialMapSettings: {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        zoom: 4
      },
      
      // Risk comparison controls
      comparisonEnabled: false,
      comparisonScope: 'state',
      
      // Error handling
      apiErrorCount: 0,
      lastApiError: null,
      comparisonFallbackActive: false,
      
      // Debouncing
      loadMapDataDebounceTimer: null,
      pendingApiRequest: null,
      
      // Crime filter data
      selectedCrimeTypes: [],
      availableCrimeTypes: [
        { value: 'total_crime', label: 'Total Crime' },
        { value: 'personal_crime', label: 'Personal Crime' },
        { value: 'property_crime', label: 'Property Crime' },
        { value: 'murder', label: 'Murder' },
        { value: 'rape', label: 'Rape' },
        { value: 'robbery', label: 'Robbery' },
        { value: 'assault', label: 'Assault' },
        { value: 'burglary', label: 'Burglary' },
        { value: 'larceny', label: 'Larceny' },
        { value: 'motor_vehicle_theft', label: 'Motor Vehicle Theft' }
      ],
      
      // Hover popup configuration (from config)
      hoverPopupOffsetX: 15, // Horizontal offset from cursor (pixels to the right)
      hoverPopupOffsetY: -10, // Vertical offset from cursor (negative = above cursor)
      hoverPopupZIndex: 40,  // From config/cresiteiq2.php hover_popup.z_index
      
      // Hover functionality
      showHoverPopover: false,
      hoverPopoverData: {
        unitCode: '',
        unitType: '',
        crimeData: {},
        riskLevel: null,
        riskPercentile: null,
        perCapitaRate: null,
        comparisonReference: null,
        position: { x: 0, y: 0 }
      },
      hoverTimeout: null,
      
      // Crime legend
      showCrimeLegend: false,
      // Crime filter modal
      showCrimeFilterModal: false,
      // Crime data opacity control
      crimeDataOpacity: 0.5, // Default opacity (50%) - balanced visibility
      opacityPresets: [
        { label: 'Light', value: 0.3 },
        { label: 'Medium', value: 0.5 },
        { label: 'Strong', value: 1.0 }
      ],
      
      // AGS data properties
      showAgsFilterModal: false,
      agsPolygonLayers: [], // Separate array for AGS-specific polygons
      showAgsLegend: false,
      agsColorScale: [], // AGS color scale for legend
      currentAgsRange: { min: 0, max: 100 },
      
      // AGS loading and error states
      agsLoading: false,
      agsError: null,
      agsLoadingMessage: 'Loading AGS data...',
      agsRetryCount: 0,
      maxAgsRetries: 2,
      
      // AGS hover functionality
      showAgsHoverPopover: false,
      agsHoverPopoverData: {
        unitCode: '',
        unitType: '',
        unitName: '', // Geographic unit name (e.g., "Nevada" for state)
        agsData: {},
        selectedVariables: [],
        position: { x: 0, y: 0 }
      },
      // Popup data
      showPopup: false,
      showAgsPopup: false,
      popupData: {
        unitCode: '',
        unitType: '',
        unitName: '',
        stateCode: '',
        countyCode: '',
        tractCode: '',
        blockGroupCode: '',
        populationData: null,
        crimeData: {},
        agsData: {},
        agsIntensityLevel: null,
        agsRiskLevel: null,
        agsValue: null,
        agsComparisonReference: null,
        combinedValue: 0,
        intensityPercent: 0
      },
      
      // Details popup state tracking (for auto-close functionality)
      lastZoomLevel: null // Track zoom level changes for popup auto-close
    }
  },
  
  created() {
    // Use singleton AGS store
    this.agsStore = agsStore;
  },
  
  computed: {
    // Track if any details popup is currently open
    detailsPopupOpen() {
      return this.showPopup || this.showAgsPopup;
    },
    
    showCrimeFilters() {
      return cresiteiq2Api.shouldShowCrimeFilters(this.currentZoom);
    },
    
    /**
     * Determine if comparison section should be shown
     * Shows when:
     * 1. AGS data is selected AND current zoom is at state or county level (below ZIP level), OR
     * 2. Crime filters are enabled (legacy behavior)
     */
    shouldShowComparisonSection() {
      const hasAgsData = this.selectedAgsVariables.length > 0;
      const comparisonLevel = this.currentZoom > ZOOM_THRESHOLDS.COUNTY_MAX;
      const hasCrimeFilters = this.showCrimeFilters;
      
      return (hasAgsData && comparisonLevel) || hasCrimeFilters;
    },
    
    // Get selected AGS variables from store
    selectedAgsVariables() {
      const variables = this.agsStore ? this.agsStore.selectedVariables : [];
      console.log('🗺️ Map Page: selectedAgsVariables computed called:', {
        variables: variables,
        count: variables.length,
        agsStoreExists: !!this.agsStore,
        storeInstance: this.agsStore
      });
      return variables;
    },
    
    // Get selected AGS categories from store
    selectedAgsCategories() {
      const categories = this.agsStore ? this.agsStore.selectedCategories : [];
      return categories;
    },
    
    // Get AGS data opacity from store
    agsDataOpacity() {
      return this.agsStore ? this.agsStore.agsDataOpacity : 0.5;
    },
    
    // Get AGS scale reversed state from store
    agsScaleReversed() {
      return this.agsStore ? this.agsStore.agsScaleReversed : false;
    },
    
    activeLayerColors() {
      const defaultColors = { fill: '#3B82F6', border: '#1D4ED8' };
      const match = this.layerColorLegend.find(layer => layer.id === this.activeLayer);
      return match ? { fill: match.fill, border: match.border } : defaultColors;
    },
    crimeColorScale() {
      // Use 7-level risk-based color scale for better granularity
      return [
        { color: '#059669', label: 'Very Low Risk' },      // Dark green (0-14%)
        { color: '#22c55e', label: 'Low Risk' },           // Green (14-28%)
        { color: '#84cc16', label: 'Low-Moderate Risk' },  // Yellow-green (28-42%)
        { color: '#eab308', label: 'Moderate Risk' },      // Yellow (42-57%)
        { color: '#f97316', label: 'Moderate-High Risk' }, // Orange (57-71%)
        { color: '#ef4444', label: 'High Risk' },          // Red (71-85%)
        { color: '#dc2626', label: 'Very High Risk' },     // Dark red (85-100%)
        { color: '#9ca3af', label: 'No Data' }             // Gray
      ];
    },
    zoomSliderStyle() {
      const { fill, border } = this.activeLayerColors;
      const min = this.zoomSlider.min;
      const max = this.zoomSlider.max;
      const value = Math.min(Math.max(this.zoomSliderValue, min), max);
      const percent = max === min ? 0 : ((value - min) / (max - min)) * 100;

      return {
        '--zoom-thumb-color': fill,
        '--zoom-thumb-border': border,
        '--zoom-track-active': fill,
        '--zoom-track-inactive': '#cbd5e1',
        '--zoom-progress': `${percent}%`
      };
    },
    
    zoomThumbPosition() {
      // Calculate thumb position for the vertical zoom indicator (80px total height, 8px padding top/bottom)
      const trackHeight = 64; // 80px - 16px padding
      const min = this.zoomSlider.min;
      const max = this.zoomSlider.max;
      const value = Math.min(Math.max(this.currentZoom, min), max);
      const percent = max === min ? 0 : ((value - min) / (max - min));
      
      // Invert the position (higher zoom = higher on track)
      return 8 + (trackHeight * (1 - percent));
    },
    
    zoomLevelLayers() {
      return [
        {
          name: 'Blocks',
          zoomRange: `${ZOOM_THRESHOLDS.BLOCK_MIN}+`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.BLOCK_MIN,
          color: '#3B82F6',
          borderColor: '#1D4ED8'
        },
        {
          name: 'Block Groups', 
          zoomRange: `${ZOOM_THRESHOLDS.BG_MIN}-${ZOOM_THRESHOLDS.BG_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.BG_MIN && this.currentZoom <= ZOOM_THRESHOLDS.BG_MAX,
          color: '#3B82F6',
          borderColor: '#1D4ED8'
        },
        {
          name: 'Census Tracts',
          zoomRange: `${ZOOM_THRESHOLDS.TRACT_MIN}-${ZOOM_THRESHOLDS.TRACT_MAX}`, 
          active: this.currentZoom >= ZOOM_THRESHOLDS.TRACT_MIN && this.currentZoom <= ZOOM_THRESHOLDS.TRACT_MAX,
          color: '#3B82F6',
          borderColor: '#1D4ED8'
        },
        {
          name: 'ZIP Codes',
          zoomRange: `${ZOOM_THRESHOLDS.ZIP_MIN}-${ZOOM_THRESHOLDS.ZIP_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.ZIP_MIN && this.currentZoom <= ZOOM_THRESHOLDS.ZIP_MAX,
          color: '#3B82F6',
          borderColor: '#1D4ED8'
        },
        {
          name: 'Counties (Not Available)',
          zoomRange: `${ZOOM_THRESHOLDS.COUNTY_MIN}-${ZOOM_THRESHOLDS.COUNTY_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.COUNTY_MIN && this.currentZoom <= ZOOM_THRESHOLDS.COUNTY_MAX,
          color: '#9CA3AF',
          borderColor: '#6B7280'
        },
        {
          name: 'States',
          zoomRange: `${ZOOM_THRESHOLDS.STATE_MIN}-${ZOOM_THRESHOLDS.STATE_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.STATE_MIN && this.currentZoom <= ZOOM_THRESHOLDS.STATE_MAX,
          color: '#3B82F6',
          borderColor: '#1D4ED8'
        }
      ];
    }
  },
  
  watch: {
    // Watch for zoom level changes and close details popup
    currentZoom(newZoom, oldZoom) {
      // Close details popup when zoom level changes
      if (newZoom !== oldZoom && this.detailsPopupOpen) {
        if (this.showPopup) {
          this.closePopup();
        }
        if (this.showAgsPopup) {
          this.closeAgsPopup();
        }
      }
      
      // Update lastZoomLevel for change detection
      this.lastZoomLevel = oldZoom;
    },
    
    comparisonEnabled() {
      // Reset fallback state when user manually changes comparison mode
      this.comparisonFallbackActive = false;
      
      // Close popup when comparison mode changes so user sees updated data on next click
      if (this.showPopup) {
        this.closePopup();
      }
      
      // Reload map data when comparison mode is toggled (with debouncing)
      if (this.showCrimeFilters || this.selectedAgsVariables.length > 0) {
        this.debouncedLoadMapData();
      }
    },
    comparisonScope() {
      // Reset fallback state when user manually changes comparison scope
      this.comparisonFallbackActive = false;
      
      // Close popup when comparison scope changes so user sees updated data on next click
      if (this.showPopup) {
        this.closePopup();
      }
      
      // Reload map data when comparison scope changes (only if comparison is enabled, with debouncing)
      if (this.comparisonEnabled && (this.showCrimeFilters || this.selectedAgsVariables.length > 0)) {
        this.debouncedLoadMapData();
      }
    },
    selectedCrimeTypes: {
      handler() {
        if (this.showCrimeFilters) {
          this.debouncedLoadMapData();
        }
      },
      deep: true
    },

    showCrimeFilters(newVal) {
      if (!newVal) {
        this.showCrimeScaleOverlay = false;
      }
    },
    
    selectedAgsVariables: {
      handler() {
        // Close details popup when AGS filters change
        if (this.detailsPopupOpen) {
          if (this.showPopup) {
            this.closePopup();
          }
          if (this.showAgsPopup) {
            this.closeAgsPopup();
          }
        }
        
        // Reload map data when AGS variables change (debounced)
        this.debouncedLoadMapData();
      },
      deep: true
    },
    crimeDataOpacity: {
      handler() {
        // Update polygon opacity when user changes the slider
        this.updatePolygonOpacity();
      }
    },
    agsDataOpacity: {
      handler() {
        // Update AGS polygon opacity when user changes the slider
        this.updateAgsPolygonOpacity();
      }
    },
    agsScaleReversed: {
      handler(newValue, oldValue) {
        console.log('🔄 agsScaleReversed watcher triggered:', {
          oldValue,
          newValue,
          hasAgsVariables: this.selectedAgsVariables.length > 0,
          hasPolygons: this.agsPolygonLayers?.length > 0
        });
        
        // Rebuild AGS color scale when scale direction changes
        this.buildAgsColorScale();
        // Update existing AGS polygon colors
        this.updateAgsPolygonColors();
      }
    },
    
    // Watch for scale direction changes (deep watch on the object)
    'agsStore.scaleDirections': {
      handler(newValue, oldValue) {
        console.log('🔄 Scale directions changed:', {
          newValue,
          oldValue,
          hasAgsVariables: this.selectedAgsVariables.length > 0,
          isMultiVariable: this.selectedAgsVariables.length >= 2
        });
        
        // Only reload if we have 2+ variables (multi-variable mode)
        if (this.selectedAgsVariables.length >= 2) {
          this.debouncedLoadMapData();
        }
      },
      deep: true
    }
  },
  
  mounted() {
    this.waitForGoogleMaps();
  },
  
  beforeDestroy() {
    this.clearOverlayHoverTimer();
    this.clearHoverTimeout();
    this.clearLoadMapDataDebounce();
    // Cancel any pending API requests
    if (this.pendingApiRequest) {
      this.pendingApiRequest = null;
    }
    // Clean up temporary marker
    if (this.temporaryMarker) {
      this.temporaryMarker.setMap(null);
      this.temporaryMarker = null;
    }
  },
  
  beforeUnmount() {
    this.clearOverlayHoverTimer();
    this.clearHoverTimeout();
    // Clean up temporary marker
    if (this.temporaryMarker) {
      this.temporaryMarker.setMap(null);
      this.temporaryMarker = null;
    }
  },
  
  methods: {
    waitForGoogleMaps() {
      // Check if Google Maps API is already loaded
      if (typeof google !== 'undefined' && google.maps) {
        this.initializeMap();
        return;
      }
      
      // Wait for Google Maps API to load
      const checkGoogleMaps = () => {
        if (typeof google !== 'undefined' && google.maps) {
          this.initializeMap();
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };
      
      checkGoogleMaps();
    },
    
    initializeMap() {
      // Initialize Google Maps
      this.map = new google.maps.Map(document.getElementById('cresiteiq2-map'), {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        zoom: 4,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'administrative',
            elementType: 'geometry',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
      
      this.syncZoomSlider();
      
      // Add event listeners
      this.map.addListener('idle', () => {
        this.onMapIdle();
      });
      
      this.map.addListener('zoom_changed', () => {
        this.currentZoom = this.map.getZoom();
        this.syncZoomSlider();
        this.updateActiveLayer();
      });
      
      // Wait for map to be fully loaded before initial data load
      google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
        this.onMapIdle();
      });
    },
    
    onMapIdle() {
      this.currentZoom = this.map.getZoom();
      this.syncZoomSlider();
      const bounds = this.map.getBounds();
      
      // Only proceed if bounds are valid
      if (bounds && bounds.getNorthEast && bounds.getSouthWest) {
        this.currentBounds = bounds;
        this.updateActiveLayer();
        this.debouncedLoadMapData();
      } else {
        console.warn('Map bounds not ready yet, skipping data load');
      }
    },
    
    updateActiveLayer() {
      this.activeLayer = cresiteiq2Api.determineActiveLayer(this.currentZoom);
    },
    
    // Method removed - crime data is now always enabled at appropriate zoom levels

    onCrimeFiltersUpdated(selectedCrimeTypes) {
      this.selectedCrimeTypes = selectedCrimeTypes;
      this.showCrimeFilterModal = false;
      // Reload map data with new filters (debounced)
      if (this.showCrimeFilters) {
        this.debouncedLoadMapData();
      }
    },

    removeCrimeFilter(crimeTypeToRemove) {
      this.selectedCrimeTypes = this.selectedCrimeTypes.filter(
        crimeType => crimeType !== crimeTypeToRemove
      );
      
      // Reload map data with updated filters
      if (this.showCrimeFilters) {
        this.loadMapData();
      }
    },

    clearAllCrimeFilters() {
      this.selectedCrimeTypes = [];
      
      // Reload map data with cleared filters (debounced)
      if (this.showCrimeFilters) {
        this.debouncedLoadMapData();
      }
    },

    // AGS variable management methods
    onAgsVariablesUpdated(selectedVariables) {
      console.log('🗺️ Map Page: Received AGS variables update:', {
        variables: selectedVariables,
        count: selectedVariables.length
      });
      
      // Update the AGS store
      if (this.agsStore) {
        this.agsStore.setSelectedVariables(selectedVariables);
        console.log('🗺️ Map Page: AGS store updated, current variables:', this.agsStore.selectedVariables);
      }
      this.showAgsFilterModal = false;
      
      // Reload map data with new AGS variables (debounced)
      this.debouncedLoadMapData();
    },

    removeAgsVariable(variableToRemove) {
      // Use the AGS store method
      if (this.agsStore) {
        this.agsStore.removeVariable(variableToRemove);
        // Reload map data with updated variables (debounced)
        this.debouncedLoadMapData();
      }
    },

    clearAllAgsVariables() {
      // Use the AGS store method
      if (this.agsStore) {
        this.agsStore.clearAllVariables();
        // Reload map data with cleared variables (debounced)
        this.debouncedLoadMapData();
      }
    },

    // AGS opacity control method
    onAgsOpacityChanged(newOpacity) {
      // Update AGS store opacity
      if (this.agsStore) {
        this.agsStore.setAgsDataOpacity(newOpacity);
      }
      
      // Update AGS polygon opacity
      this.updateAgsPolygonOpacity();
    },

    // AGS scale direction change handler
    onScaleDirectionChanged(data) {
      console.log('🔄 Scale direction changed:', data);
      
      // The store is already updated by the component
      
      if (data.isSingleVariable) {
        // For single variable mode, just update the color scale (reverse colors)
        // This only affects visualization, not the backend logic
        console.log('🎨 Single variable mode: Updating color scale only');
        this.buildAgsColorScale();
        this.updateAgsPolygonColors();
      } else {
        // For multi-variable mode, reload the map data with new scale directions
        // This affects the backend averaging calculation
        console.log('🔄 Multi-variable mode: Reloading map data with new scale directions');
        this.debouncedLoadMapData();
      }
    },

    // AGS weights change handler
    onWeightsChanged(weights) {
      console.log('⚖️ Weights changed:', weights);
      
      // Update the AGS store with new weights
      if (this.agsStore) {
        this.agsStore.setWeights(weights);
      }
      
      // Reload the map data if we're in multi-variable mode
      if (this.selectedAgsVariables.length >= 2) {
        this.debouncedLoadMapData();
      }
    },

    /**
     * Set AGS loading state with optional message
     */
    setAgsLoading(loading, message = 'Loading AGS data...') {
      this.agsLoading = loading;
      this.agsLoadingMessage = message;
      
      if (loading) {
        // Clear any existing error when starting new load
        this.agsError = null;
      }
    },

    /**
     * Set AGS error state
     */
    setAgsError(error) {
      this.agsError = error;
      this.agsLoading = false;
      
      console.error('AGS Error:', error);
    },

    /**
     * Clear AGS error state
     */
    dismissAgsError() {
      this.agsError = null;
      this.agsRetryCount = 0;
    },

    /**
     * Retry AGS data loading
     */
    async retryAgsData() {
      if (this.agsRetryCount >= this.maxAgsRetries) {
        console.warn('Maximum AGS retry attempts reached');
        return;
      }

      this.agsRetryCount++;
      this.agsError = null;
      
      console.log(`Retrying AGS data load (attempt ${this.agsRetryCount}/${this.maxAgsRetries})`);
      
      // Set loading message to indicate retry
      this.setAgsLoading(true, `Retrying AGS data... (${this.agsRetryCount}/${this.maxAgsRetries})`);
      
      // Retry the map data load
      try {
        await this.loadMapData();
      } catch (error) {
        this.setAgsError(`Retry failed: ${error.message}`);
      }
    },

    /**
     * Handle AGS-specific loading progress
     */
    updateAgsLoadingProgress(variablesLoaded, totalVariables) {
      if (totalVariables > 0) {
        const percentage = Math.round((variablesLoaded / totalVariables) * 100);
        this.setAgsLoading(true, `Loading AGS data... ${percentage}% (${variablesLoaded}/${totalVariables} variables)`);
      }
    },

    // Update AGS polygon opacity
    updateAgsPolygonOpacity() {
      if (this.selectedAgsVariables.length === 0) {
        return;
      }

      if (!this.agsPolygonLayers || this.agsPolygonLayers.length === 0) {
        return;
      }

      const baseOpacity = this.getBaseAgsOpacityForLayer();
      const newOpacity = baseOpacity * this.agsDataOpacity;

      this.agsPolygonLayers.forEach(polygon => {
        polygon.setOptions({ fillOpacity: newOpacity });

        if (polygon.originalStyles) {
          polygon.originalStyles.fillOpacity = newOpacity;
        }
      });
    },

    /**
     * Update AGS polygon colors when scale direction changes
     * Re-applies colors to existing polygons based on their intensity level
     */
    updateAgsPolygonColors() {
      console.log('🎨 updateAgsPolygonColors called', {
        selectedVariablesCount: this.selectedAgsVariables.length,
        polygonCount: this.agsPolygonLayers?.length || 0,
        agsScaleReversed: this.agsScaleReversed
      });
      
      if (this.selectedAgsVariables.length === 0) {
        console.log('⚠️ No AGS variables selected, skipping color update');
        return;
      }

      if (!this.agsPolygonLayers || this.agsPolygonLayers.length === 0) {
        console.log('⚠️ No AGS polygon layers found, skipping color update');
        return;
      }

      let updatedCount = 0;
      let missingIntensityCount = 0;
      
      this.agsPolygonLayers.forEach((polygon, index) => {
        // Get the stored intensity level from the polygon
        const intensityLevel = polygon.get('intensityLevel');
        
        if (index === 0) {
          console.log('🔍 First polygon check:', {
            hasIntensityLevel: !!intensityLevel,
            intensityLevel: intensityLevel,
            polygonKeys: Object.keys(polygon)
          });
        }
        
        if (intensityLevel) {
          // Get the new color based on current scale direction
          console.log('🔵 ABOUT TO CALL getAgsIntensityColor with:', intensityLevel);
          const newColor = this.agsStore.getAgsIntensityColor(intensityLevel);
          console.log('🟢 getAgsIntensityColor RETURNED:', newColor);
          
          if (index === 0) {
            console.log('🎨 Updating first polygon color:', {
              intensityLevel,
              newColor,
              agsScaleReversed: this.agsScaleReversed
            });
          }
          
          // Update the polygon fill color
          polygon.setOptions({ fillColor: newColor });
          
          // Update stored original styles
          if (polygon.originalStyles) {
            polygon.originalStyles.fillColor = newColor;
          }
          
          updatedCount++;
        } else {
          missingIntensityCount++;
        }
      });
      
      console.log('✅ AGS polygon colors updated:', {
        totalPolygons: this.agsPolygonLayers.length,
        updatedCount,
        missingIntensityCount
      });
    },

    /**
     * Get base AGS opacity for the current layer
     * @returns {number} Base opacity value (0.0 to 1.0)
     */
    /**
     * Toggle AGS scale direction between ascending and descending
     */
    toggleAgsScale() {
      if (this.agsStore) {
        this.agsStore.toggleAgsScale();
      }
    },

    // Debounced map data loading


    // Opacity control methods
    onOpacityChange() {
      // Update opacity of existing polygons
      this.updatePolygonOpacity();
    },

    setOpacityPreset(value) {
      this.crimeDataOpacity = value;
      this.updatePolygonOpacity();
    },

    updatePolygonOpacity() {
      // Update opacity for all existing polygons
      this.polygonLayers.forEach(polygon => {
        const currentFillOpacity = polygon.get('fillOpacity');
        if (currentFillOpacity && this.selectedCrimeTypes.length > 0) {
          // Calculate base opacity for the current layer
          const baseOpacity = this.getBaseOpacityForLayer();
          // Apply user-controlled opacity multiplier
          const newOpacity = baseOpacity * this.crimeDataOpacity;
          polygon.setOptions({ fillOpacity: newOpacity });
        }
      });
    },

    getBaseOpacityForLayer() {
      // Base opacity values for different layers (before user multiplier)
      // These values are designed so that when multiplied by 1.0 (100% user opacity),
      // the result will be 1.0 (fully opaque) to completely hide map labels
      const baseOpacities = {
        'States': 1.0,          // Will be 1.0 at 100% user opacity (hides map labels)
        'Counties': 1.0,        // Will be 1.0 at 100% user opacity (hides map labels)
        'ZIP Codes': 1.0,       // Will be 1.0 at 100% user opacity (hides map labels)
        'Census Tracts': 1.0,   // Will be 1.0 at 100% user opacity (hides map labels)
        'Block Groups': 1.0,    // Will be 1.0 at 100% user opacity (hides map labels)
        'Blocks': 1.0           // Will be 1.0 at 100% user opacity (hides map labels)
      };
      
      return baseOpacities[this.activeLayer] || 1.0;
    },

    onZoomSliderInput(event) {
      const rawValue = Number(event.target.value);
      if (Number.isNaN(rawValue)) {
        return;
      }

      const clamped = Math.max(this.zoomSlider.min, Math.min(this.zoomSlider.max, Math.round(rawValue)));
      if (this.zoomSliderValue !== clamped) {
        this.zoomSliderValue = clamped;
      }

      if (this.map && this.map.getZoom() !== clamped) {
        this.map.setZoom(clamped);
      } else if (!this.map) {
        this.currentZoom = clamped;
      }
    },

    syncZoomSlider() {
      const clamped = Math.max(this.zoomSlider.min, Math.min(this.zoomSlider.max, Math.round(this.currentZoom)));
      this.zoomSliderValue = clamped;
    },

    // New zoom control methods
    zoomIn() {
      if (this.map && this.currentZoom < this.zoomSlider.max) {
        this.map.setZoom(this.currentZoom + 1);
      }
    },

    zoomOut() {
      if (this.map && this.currentZoom > this.zoomSlider.min) {
        this.map.setZoom(this.currentZoom - 1);
      }
    },

    toggleCrimeScaleOverlay() {
      if (!this.showCrimeFilters || this.selectedCrimeTypes.length === 0) {
        this.showCrimeScaleOverlay = false;
        return;
      }
      this.showCrimeScaleOverlay = !this.showCrimeScaleOverlay;
    },
    handleOverlayEnter() {
      this.clearOverlayHoverTimer();
      this.showLayerLegendOverlay = true;
    },

    handleOverlayLeave() {
      this.clearOverlayHoverTimer();
      this.overlayHoverTimeout = setTimeout(() => {
        this.showLayerLegendOverlay = false;
        this.overlayHoverTimeout = null;
      }, 200);
    },

    clearOverlayHoverTimer() {
      if (this.overlayHoverTimeout) {
        clearTimeout(this.overlayHoverTimeout);
        this.overlayHoverTimeout = null;
      }
    },

    /**
     * Debounced version of loadMapData to prevent excessive API calls
     * Waits 300ms after the last call before executing
     */
    debouncedLoadMapData() {
      // Clear any existing debounce timer
      this.clearLoadMapDataDebounce();
      
      // Set new debounce timer
      this.loadMapDataDebounceTimer = setTimeout(() => {
        this.loadMapData();
      }, 300);
    },

    /**
     * Clear the debounce timer for loadMapData
     */
    clearLoadMapDataDebounce() {
      if (this.loadMapDataDebounceTimer) {
        clearTimeout(this.loadMapDataDebounceTimer);
        this.loadMapDataDebounceTimer = null;
      }
    },

    async loadMapData() {
      if (!this.currentBounds) {
        console.warn('No current bounds available, skipping data load');
        return;
      }
      
      // Clear existing polygons first
      this.clearPolygons();
      
      // Only make API calls when zoom >= 4 (states and above), but skip counties (6-7)
      if (this.currentZoom < 4) {
        console.log('Zoom level below 4, skipping API call');
        return;
      }
      
      // Skip counties (zoom 6-7) as we don't have county data yet
      if (this.currentZoom >= 6 && this.currentZoom < 8) {
        console.log('Skipping counties (zoom 6-7) - no data available');
        return;
      }
      
      // Cancel any pending API request
      if (this.pendingApiRequest) {
        this.pendingApiRequest = null;
      }
      
      // Initialize params object early to avoid undefined reference in error handling
      let params = {
        zoom: this.currentZoom,
        layer: 'auto',
        includeCrime: this.selectedCrimeTypes.length > 0
      };
      
      // Show loading indicator
      this.loading = true;
      
      try {
        // Validate bounds before building bbox string
        if (!this.currentBounds.getNorthEast || !this.currentBounds.getSouthWest) {
          throw new Error('Invalid bounds object - missing required methods');
        }
        
        const bboxString = cresiteiq2Api.buildBboxString(this.currentBounds);
        console.log('Built bbox string:', bboxString);
        
        // Update params with bbox
        params.bbox = bboxString;
        
        // Add crime type filters if applicable
        if (this.showCrimeFilters && this.selectedCrimeTypes.length > 0) {
          params.crimeTypes = this.selectedCrimeTypes.join(',');
        }

        // Add AGS data parameters if variables are selected
        if (this.selectedAgsVariables.length > 0) {
          params.includeAgs = 1;
          
          // Get unique category names from selected variables
          const categoryNames = [...new Set(this.selectedAgsVariables.map(v => v.category_name))];
          params.agsCategories = categoryNames.join(',');
          
          // Get variable paths for the API
          const variablePaths = this.selectedAgsVariables.map(v => v.path);
          params.agsVariable = variablePaths.join(',');
          
          console.log('🔍 AGS Variable Paths Debug:', {
            selectedAgsVariables: this.selectedAgsVariables,
            variablePaths: variablePaths,
            agsVariable: params.agsVariable
          });
          
          // Add scale directions and weights for multi-variable mode (2+ variables)
          if (this.selectedAgsVariables.length >= 2 && this.agsStore) {
            const scaleDirections = this.agsStore.getScaleDirectionsForAPI();
            const weights = this.agsStore.getWeightsForAPI();
            
            // Convert boolean values to integers for API compatibility
            // Laravel expects 0/1 in query parameters, not "true"/"false" strings
            params.scaleDirections = {};
            Object.keys(scaleDirections).forEach(key => {
              params.scaleDirections[key] = scaleDirections[key] ? 1 : 0;
            });
            
            params.weights = weights;
            
            console.log('🔍 Multi-variable AGS parameters:', {
              variableCount: this.selectedAgsVariables.length,
              scaleDirections: params.scaleDirections,
              weights: params.weights
            });
          }
          
          // Add other AGS parameters
          params.agsTemporalPeriod = '2025'; // Default period
          
          // Set AGS comparison mode based on risk comparison settings
          if (this.comparisonEnabled) {
            params.agsComparisonMode = this.comparisonScope; // 'state' or 'county'
          } else {
            params.agsComparisonMode = 'viewport'; // Default comparison mode
          }
          
          // Set AGS loading state with complexity-aware messaging
          const complexity = this.selectedAgsVariables.length;
          let loadingMessage = `Loading AGS data for ${categoryNames.length} categor${categoryNames.length !== 1 ? 'ies' : 'y'}...`;
          
          if (complexity > 50) {
            loadingMessage = `Loading large AGS dataset (${complexity} variables). This may take 30-60 seconds...`;
          } else if (complexity > 20) {
            loadingMessage = `Loading AGS data (${complexity} variables). This may take 10-30 seconds...`;
          }
          
          this.setAgsLoading(true, loadingMessage);
          
          // Log AGS request details for debugging
          console.log('🗺️ AGS Data Request:', {
            includeAgs: params.includeAgs,
            agsCategories: params.agsCategories,
            agsVariable: params.agsVariable,
            categoryNames: categoryNames,
            totalVariables: this.selectedAgsVariables.length,
            agsTemporalPeriod: params.agsTemporalPeriod,
            agsComparisonMode: params.agsComparisonMode,
            comparisonEnabled: this.comparisonEnabled,
            comparisonScope: this.comparisonScope
          });
        } else {
          // Clear AGS loading state if no variables selected
          this.setAgsLoading(false);
        }

        // Add comparison mode parameters
        if (this.comparisonEnabled && this.showCrimeFilters) {
          params.comparisonMode = this.comparisonScope; // 'state' or 'county'
          // Use the first selected crime type for risk calculation
          // If multiple types selected, use the first one (most important to user)
          params.crimeType = this.selectedCrimeTypes.length > 0 
            ? this.selectedCrimeTypes[0] 
            : 'total_crime';
          console.log('🌐 API Request - Comparison Mode:', {
            comparisonEnabled: this.comparisonEnabled,
            comparisonScope: this.comparisonScope,
            comparisonMode: params.comparisonMode,
            crimeType: params.crimeType,
            selectedCrimeTypes: this.selectedCrimeTypes
          });
        } else {
          params.comparisonMode = 'viewport'; // Default viewport-based comparison
          // Use first selected crime type for viewport mode too
          params.crimeType = this.selectedCrimeTypes.length > 0 
            ? this.selectedCrimeTypes[0] 
            : 'total_crime';
        }
        
        // Track this request
        const currentRequest = {};
        this.pendingApiRequest = currentRequest;
        
        // Set up timeout warning for large AGS requests
        let timeoutWarningTimer = null;
        let finalWarningTimer = null;
        
        if (params.includeAgs && this.selectedAgsVariables.length > 20) {
          // First warning after 30 seconds
          timeoutWarningTimer = setTimeout(() => {
            if (this.agsLoading) {
              this.setAgsLoading(true, 'Large dataset is taking longer than expected. Please wait...');
              this.$toast.info('AGS data processing is taking longer than usual. Please be patient.', {
                timeout: 8000,
                position: 'top-center'
              });
            }
          }, 30000);
          
          // Final warning after 60 seconds
          finalWarningTimer = setTimeout(() => {
            if (this.agsLoading) {
              this.setAgsLoading(true, 'Processing very large dataset. This may take up to 2 minutes...');
              this.$toast.warning('Large AGS dataset is still processing. You can wait or try selecting fewer variables.', {
                timeout: 10000,
                position: 'top-center'
              });
            }
          }, 60000);
        }
        
        // Log final API parameters for debugging
        console.log('🌐 Final API Request Parameters:', params);
        
        // Make API request
        const geoJsonData = await cresiteiq2Api.getLayers(params);
        
        // Clear timeout warnings if request completes
        if (timeoutWarningTimer) {
          clearTimeout(timeoutWarningTimer);
        }
        if (finalWarningTimer) {
          clearTimeout(finalWarningTimer);
        }
        
        // Check if this request is still the current one (not cancelled)
        if (this.pendingApiRequest !== currentRequest) {
          console.log('Request cancelled, ignoring response');
          return;
        }
      
        // Check for fallback warnings in metadata
        if (geoJsonData.metadata && geoJsonData.metadata.fallback_occurred) {
          this.handleComparisonFallback(geoJsonData.metadata);
        } else {
          // Clear fallback state if successful
          this.comparisonFallbackActive = false;
        }
        
        this.renderPolygons(geoJsonData);
        
        // Hide hover popover when data changes
        this.hideHoverPopover();
        
        // Reset error count on successful load
        this.apiErrorCount = 0;
        this.lastApiError = null;
        
        // Clear AGS loading state on success
        this.setAgsLoading(false);
        this.agsRetryCount = 0; // Reset retry count on success
        
      } catch (error) {
        this.handleApiError(error, params);
      } finally {
        // Only hide loading if this is still the current request
        if (this.pendingApiRequest) {
          this.loading = false;
          this.pendingApiRequest = null;
        }
        
        // Always clear AGS loading state in finally block
        this.setAgsLoading(false);
      }
    },

    /**
     * Handle API errors with fallback to viewport mode (enhanced for AGS data)
     */
    handleApiError(error, params) {
      console.error('Error loading map data:', error);
      
      // Increment error count
      this.apiErrorCount++;
      this.lastApiError = error;
      
      // Log error details for debugging
      console.error('API Error Details:', {
        message: error.message,
        params: params,
        errorCount: this.apiErrorCount,
        timestamp: new Date().toISOString(),
        hasAgsData: params.includeAgs || false,
        agsCategories: params.agsCategories || 'none'
      });
      
      // Check if this was an AGS-related error
      const wasAgsRequest = params.includeAgs && params.agsCategories;
      const wasComparisonMode = params.comparisonMode && params.comparisonMode !== 'viewport';
      
      // Handle AGS-specific errors
      if (wasAgsRequest && this.shouldFallbackFromAgs(error)) {
        this.fallbackFromAgsData(error, params);
      } else if (wasComparisonMode && !this.comparisonFallbackActive) {
        this.fallbackToViewportMode(error);
      } else {
        // Display error notification to user
        this.displayErrorNotification(error, wasComparisonMode, wasAgsRequest);
      }
    },

    /**
     * Check if we should fallback from AGS data loading
     */
    shouldFallbackFromAgs(error) {
      // Fallback from AGS if timeout, server error, or AGS-specific issues
      const shouldFallback = error.type === 'timeout' || 
             error.type === 'server_error' ||
             error.message?.includes('timeout') ||
             error.message?.includes('Maximum execution time') ||
             error.message?.includes('500') ||
             error.message?.includes('503') ||
             error.message?.includes('502') ||
             error.message?.includes('504') ||
             error.message?.includes('408') || // Request timeout
             (error.message && error.message.toLowerCase().includes('ags')) ||
             error.status >= 500 ||
             error.status === 408; // Request timeout status
             
      console.log('AGS fallback check:', {
        shouldFallback,
        errorType: error.type,
        errorMessage: error.message,
        errorStatus: error.status,
        isTimeoutError: error.message?.includes('timeout') || error.message?.includes('Maximum execution time')
      });
      
      return shouldFallback;
    },

    /**
     * Fallback from AGS data when AGS loading fails
     */
    async fallbackFromAgsData(error, originalParams) {
      console.warn('Falling back from AGS data due to error:', error.message);
      
      // Determine appropriate warning message based on error type
      let warningMessage = 'AGS data temporarily unavailable. Showing geographic data without AGS layers.';
      
      if (error.message?.includes('timeout')) {
        warningMessage = 'AGS data loading timed out. Try selecting fewer variables or zooming in. Showing geographic data without AGS layers.';
      } else if (error.message?.includes('404')) {
        warningMessage = 'AGS data not available for this area. Showing geographic data without AGS layers.';
      } else if (error.message?.includes('403')) {
        warningMessage = 'AGS data access restricted. Showing geographic data without AGS layers.';
      } else if (error.message?.includes('500')) {
        warningMessage = 'AGS data service temporarily down. Showing geographic data without AGS layers.';
      }
      
      // Show warning notification
      this.$toast.warning(warningMessage, {
        timeout: 6000,
        position: 'top-center'
      });
      
      // Clear AGS-related UI state
      this.showAgsLegend = false;
      this.agsColorScale = [];
      this.hideAgsHoverPopover();
      
      // Retry without AGS data
      try {
        const bboxString = cresiteiq2Api.buildBboxString(this.currentBounds);
        
        const params = {
          zoom: this.currentZoom,
          bbox: bboxString,
          layer: 'auto',
          includeCrime: this.selectedCrimeTypes.length > 0,
          comparisonMode: originalParams.comparisonMode || 'viewport'
        };
        
        // Include crime data if it was in original request
        if (this.showCrimeFilters && this.selectedCrimeTypes.length > 0) {
          params.crimeTypes = this.selectedCrimeTypes.join(',');
          params.crimeType = this.selectedCrimeTypes[0];
        }
        
        // Explicitly exclude AGS data
        params.includeAgs = false;
        
        const geoJsonData = await cresiteiq2Api.getLayers(params);
        this.renderPolygons(geoJsonData);
        
        // Reset error count on successful fallback
        this.apiErrorCount = 0;
        this.lastApiError = null;
        
        // Log successful fallback
        console.log('Successfully fell back from AGS data to geographic-only view');
        
      } catch (fallbackError) {
        console.error('Fallback from AGS data also failed:', fallbackError);
        
        // Show more severe error for complete failure
        this.$toast.error('Unable to load map data. Please refresh the page and try again.', {
          timeout: 8000,
          position: 'top-center'
        });
        
        this.displayErrorNotification(fallbackError, false, false);
      }
    },

    /**
     * Fall back to viewport mode when comparison mode fails
     */
    async fallbackToViewportMode(error) {
      console.warn('Falling back to viewport mode due to error:', error.message);
      
      // Mark fallback as active
      this.comparisonFallbackActive = true;
      
      // Show warning notification
      this.$toast.warning(
        `Risk comparison temporarily unavailable. Showing viewport-based risk levels.`,
        {
          timeout: 5000,
          position: 'top-center'
        }
      );
      
      // Retry with viewport mode
      try {
        const bboxString = cresiteiq2Api.buildBboxString(this.currentBounds);
        
        const params = {
          zoom: this.currentZoom,
          bbox: bboxString,
          layer: 'auto',
          includeCrime: this.selectedCrimeTypes.length > 0,
          comparisonMode: 'viewport' // Force viewport mode
        };
        
        if (this.showCrimeFilters && this.selectedCrimeTypes.length > 0) {
          params.crimeTypes = this.selectedCrimeTypes.join(',');
        }
        
        const geoJsonData = await cresiteiq2Api.getLayers(params);
        this.renderPolygons(geoJsonData);
        
        // Reset error count on successful fallback
        this.apiErrorCount = 0;
        
      } catch (fallbackError) {
        console.error('Fallback to viewport mode also failed:', fallbackError);
        this.displayErrorNotification(fallbackError, false);
      }
    },

    /**
     * Handle comparison fallback warnings from API
     */
    handleComparisonFallback(metadata) {
      if (!this.comparisonFallbackActive) {
        this.comparisonFallbackActive = true;
        
        const message = metadata.fallback_message || 
          'Some areas are using viewport-based risk calculation due to missing reference data.';
        
        console.warn('Comparison fallback occurred:', message);
        
        this.$toast.warning(message, {
          timeout: 5000,
          position: 'top-center'
        });
      }
    },

    /**
     * Display error notification to user (enhanced for AGS errors)
     */
    displayErrorNotification(error, wasComparisonMode, wasAgsRequest = false) {
      let errorMessage = 'Failed to load map data. Please try again.';
      let errorType = 'error';
      
      // Customize message based on error type and context
      if (error.message) {
        if (error.message.includes('timeout')) {
          if (wasAgsRequest) {
            errorMessage = 'AGS data request timed out. Try selecting fewer variables or zooming in to reduce data volume.';
            errorType = 'warning';
          } else {
            errorMessage = 'Request timed out. Try zooming in to reduce data volume.';
            errorType = 'warning';
          }
        } else if (error.message.includes('Network error') || error.message.includes('network')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
          errorType = 'error';
        } else if (error.message.includes('Authentication') || error.message.includes('401')) {
          errorMessage = 'Session expired. Please refresh the page to continue.';
          errorType = 'warning';
        } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
          errorMessage = 'Access denied. You may not have permission to view this data.';
          errorType = 'error';
        } else if (error.message.includes('404') || error.message.includes('Not Found')) {
          if (wasAgsRequest) {
            errorMessage = 'AGS data not available for this area. Geographic layers will display without AGS information.';
            errorType = 'info';
          } else {
            errorMessage = 'Data not available for this geographic area.';
            errorType = 'info';
          }
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          if (wasAgsRequest) {
            errorMessage = 'AGS data service temporarily unavailable. Geographic data will load without AGS layers.';
            errorType = 'warning';
          } else {
            errorMessage = 'Server error occurred. Please try again in a moment.';
            errorType = 'error';
          }
        } else if (error.message.toLowerCase().includes('ags')) {
          errorMessage = 'AGS data service unavailable. Geographic data will load without AGS layers.';
          errorType = 'warning';
        } else if (wasAgsRequest) {
          errorMessage = 'Failed to load AGS data. Geographic layers will display without AGS information.';
          errorType = 'warning';
        } else if (wasComparisonMode) {
          errorMessage = 'Risk comparison data unavailable. Falling back to viewport-based analysis.';
          errorType = 'warning';
        } else {
          errorMessage = error.message;
          errorType = 'error';
        }
      }
      
      // Show appropriate toast based on error type
      if (errorType === 'warning') {
        this.$toast.warning(errorMessage, {
          timeout: 6000,
          position: 'bottom-right'
        });
      } else if (errorType === 'info') {
        this.$toast.info(errorMessage, {
          timeout: 5000,
          position: 'bottom-right'
        });
      } else {
        this.$toast.error(errorMessage, {
          timeout: 5000,
          position: 'bottom-right'
        });
      }
      
      // If multiple errors, suggest action
      if (this.apiErrorCount >= 3) {
        this.$toast.info('Having trouble? Try refreshing the page or zooming to a different area.', {
          timeout: 7000,
          position: 'top-center'
        });
      }
      
      // Log error details for debugging
      console.error('Map data error details:', {
        message: error.message,
        wasAgsRequest,
        wasComparisonMode,
        errorCount: this.apiErrorCount,
        timestamp: new Date().toISOString()
      });
    },
    

    
    clearPolygons() {
      // Clear crime data polygons
      this.polygonLayers.forEach(polygon => {
        // Clean up stored styles
        if (polygon.originalStyles) {
          delete polygon.originalStyles;
        }
        polygon.setMap(null);
      });
      this.polygonLayers = [];
      
      // Clear AGS polygons
      this.agsPolygonLayers.forEach(polygon => {
        // Clean up stored styles
        if (polygon.originalStyles) {
          delete polygon.originalStyles;
        }
        polygon.setMap(null);
      });
      this.agsPolygonLayers = [];
      
      // Close any open info windows
      if (this.infoWindow) {
        this.infoWindow.close();
        this.infoWindow = null;
      }
      
      // Hide hover popover and legends
      this.hideHoverPopover();
      this.showCrimeLegend = false;
      this.showAgsLegend = false;
    },
    
    renderPolygons(geoJsonData) {
      if (!geoJsonData || !geoJsonData.features) {
        console.warn('No GeoJSON data to render');
        return;
      }
      
      // Debug: Check if AGS data is present in the response
      const featuresWithAgs = geoJsonData.features.filter(f => f.properties.ags_data);
      console.log('🗺️ Rendering polygons:', {
        totalFeatures: geoJsonData.features.length,
        featuresWithAgs: featuresWithAgs.length,
        selectedAgsVariables: this.selectedAgsVariables.length,
        sampleAgsData: featuresWithAgs.length > 0 ? featuresWithAgs[0].properties.ags_data : 'No AGS data'
      });
      
      // Track processed features to avoid duplicates
      const processedCodes = new Set();
      
      geoJsonData.features.forEach(feature => {
        const geometry = feature.geometry;
        const properties = feature.properties;
        
        // Skip if we've already processed this feature
        const featureCode = properties.code || properties.id;
        if (featureCode && processedCodes.has(featureCode)) {
          console.warn('Skipping duplicate feature:', featureCode);
          return;
        }
        
        if (featureCode) {
          processedCodes.add(featureCode);
        }
        
        // Handle different geometry types properly
        if (geometry.type === 'Polygon') {
          this.createSinglePolygon(geometry, properties);
        } else if (geometry.type === 'MultiPolygon') {
          // For MultiPolygon, create a single polygon with all parts
          // This prevents overlapping rendering of the same feature
          this.createMultiPolygon(geometry, properties);
        }
      });
      
      // Update AGS legend visibility if AGS data is present
      this.updateAgsLegendVisibility(geoJsonData);
    },

    /**
     * Update AGS legend visibility based on data presence
     */
    updateAgsLegendVisibility(geoJsonData) {
      const hasAgsData = geoJsonData.features.some(feature => 
        feature.properties.ags_data && this.selectedAgsVariables.length > 0
      );
      
      console.log('🗺️ Updating AGS legend visibility:', {
        hasAgsData,
        currentZoom: this.currentZoom,
        selectedVariablesCount: this.selectedAgsVariables.length,
        featuresWithAgsData: geoJsonData.features.filter(f => f.properties.ags_data).length
      });
      
      if (hasAgsData) { // Show legend when AGS data is available
        // Build AGS color scale based on selected variables
        this.buildAgsColorScale();
        this.showAgsLegend = true;
        
        console.log('🗺️ AGS legend enabled with color scale:', this.agsColorScale);
      } else {
        this.showAgsLegend = false;
        this.agsColorScale = [];
        
        console.log('🗺️ AGS legend disabled');
      }
    },

    /**
     * Build AGS color scale for legend based on intensity levels
     * Supports scale reversal based on per-variable scale direction (single variable)
     * or global agsScaleReversed state (multi-variable)
     */
    buildAgsColorScale() {
      const labels = ['Very Low', 'Low', 'Low-Moderate', 'Moderate', 'Moderate-High', 'High', 'Very High', 'No Data'];
      
      // Ascending scale colors (default): green → red (higher is worse)
      const defaultColors = ['#059669', '#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444', '#dc2626', '#9ca3af'];
      
      // Descending scale colors (reversed): red → green (higher is better)
      const reversedColors = ['#dc2626', '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#059669', '#9ca3af'];
      
      // Determine if we should reverse colors
      let shouldReverse = false;
      
      if (this.selectedAgsVariables.length === 1) {
        // Single variable mode: use per-variable scale direction
        const variable = this.selectedAgsVariables[0];
        const scaleDirection = this.agsStore?.scaleDirections[variable.path] ?? false;
        // If scaleDirection is true (higher is better), reverse colors
        shouldReverse = scaleDirection;
      } else {
        // Multi-variable mode: use global scale reversed state
        shouldReverse = this.agsScaleReversed;
      }
      
      const colors = shouldReverse ? reversedColors : defaultColors;
      
      this.agsColorScale = labels.map((label, index) => ({
        color: colors[index],
        label: label
      }));
    },

    /**
     * Display AGS hover popover
     */
    displayAgsHoverPopover(properties, event) {
      if (!properties.ags_data || this.selectedAgsVariables.length === 0) {
        return;
      }

      console.log('🖱️ Displaying AGS hover popover:', {
        unitCode: properties.code,
        agsData: properties.ags_data,
        agsDataKeys: Object.keys(properties.ags_data || {}),
        selectedVariables: this.selectedAgsVariables,
        selectedVariablePaths: this.selectedAgsVariables.map(v => v.path)
      });

      // Clear existing hover timeout
      this.clearHoverTimeout();

      // Set timeout to show popover (debounced)
      this.hoverTimeout = setTimeout(() => {
        // Get mouse position relative to the map container
        const mapContainer = document.getElementById('cresiteiq2-map');
        const rect = mapContainer ? mapContainer.getBoundingClientRect() : this.$el.getBoundingClientRect();
        
        // Calculate position with offset using constants (no hardcoded values)
        let x, y;
        if (event.domEvent) {
          // Use domEvent for consistent positioning across all layers
          x = event.domEvent.clientX - rect.left + this.hoverPopupOffsetX;
          y = event.domEvent.clientY - rect.top - this.hoverPopupOffsetY;
        } else if (event.pixel) {
          x = event.pixel.x + this.hoverPopupOffsetX;
          y = event.pixel.y - this.hoverPopupOffsetY;
        } else {
          // Fallback position
          x = 200;
          y = 200;
        }
        
        // Ensure popover doesn't go off screen (viewport boundary checks)
        const popoverWidth = 250; // Estimated popover width
        
        if (x + popoverWidth > rect.width) {
          x = Math.max(0, x - popoverWidth - (2 * this.hoverPopupOffsetX)); // Position to the left
        }
        
        if (y < 0) {
          y = Math.max(0, y + (2 * this.hoverPopupOffsetY)); // Position below cursor
        }

        // Extract unit name for better readability
        const unitName = properties.name || properties.state_name || properties.county_name || '';
        
        // Flatten AGS data structure from { category_name: { extracted_values: { path: value } } }
        // to { path: value } for easier access
        const flattenedAgsData = {};
        if (properties.ags_data && typeof properties.ags_data === 'object') {
          Object.values(properties.ags_data).forEach(categoryData => {
            if (categoryData && categoryData.extracted_values) {
              Object.assign(flattenedAgsData, categoryData.extracted_values);
            }
          });
        }
        
        this.agsHoverPopoverData = {
          unitCode: properties.code || properties.id || 'Unknown',
          unitType: this.activeLayer,
          unitName: unitName, // Add unit name for display (e.g., "Nevada" instead of just "29")
          agsData: flattenedAgsData,
          selectedVariables: this.selectedAgsVariables.slice(0, 4), // Show first 4 variables
          position: { x, y }
        };

        this.showAgsHoverPopover = true;
        
        console.log('🖱️ AGS hover popover data set:', this.agsHoverPopoverData);
        console.log('🔍 AGS data lookup test:', {
          firstVariable: this.selectedAgsVariables[0],
          firstVariablePath: this.selectedAgsVariables[0]?.path,
          valueByPath: properties.ags_data?.[this.selectedAgsVariables[0]?.path],
          allAgsDataKeys: Object.keys(properties.ags_data || {})
        });
      }, 150);
    },

    /**
     * Hide AGS hover popover
     */
    hideAgsHoverPopover() {
      this.clearHoverTimeout();
      this.showAgsHoverPopover = false;
      this.agsHoverPopoverData = {
        unitCode: '',
        unitType: '',
        unitName: '',
        agsData: {},
        selectedVariables: [],
        position: { x: 0, y: 0 }
      };
    },

    /**
     * Format AGS value for display
     * AGS data is structured as a flat object: { variable_path: value }
     */
    formatAgsValue(value, variable) {
      // If value is passed directly (for backward compatibility)
      if (value !== undefined && value !== null && typeof value !== 'object') {
        return this.formatNumericValue(value);
      }
      
      // If variable is provided, look up the value using the variable path
      if (variable && variable.path) {
        // AGS data is structured as a flat object: { variable_path: value }
        const agsData = this.agsHoverPopoverData.agsData;
        
        if (agsData && typeof agsData === 'object') {
          // Get value directly by variable path
          const valueByPath = agsData[variable.path];
          
          if (valueByPath !== undefined && valueByPath !== null) {
            // Exclude zero values (they represent missing data)
            if (valueByPath === 0 || valueByPath === 0.0) {
              return 'N/A';
            }
            return this.formatNumericValue(valueByPath);
          }
        }
      }
      
      return 'N/A';
    },
    
    /**
     * Format numeric values with appropriate units
     */
    formatNumericValue(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
      }
      
      const numValue = Number(value);
      
      // Format numbers with appropriate precision
      if (numValue >= 1000000) {
        return (numValue / 1000000).toFixed(1) + 'M';
      } else if (numValue >= 1000) {
        return (numValue / 1000).toFixed(1) + 'K';
      } else if (numValue % 1 === 0) {
        return numValue.toString();
      } else {
        return numValue.toFixed(2);
      }
    },
    
    createMultiPolygon(geometry, properties) {
      // Convert all MultiPolygon parts to Google Maps format
      const allPaths = [];
      
      geometry.coordinates.forEach(polygonCoords => {
        const paths = polygonCoords.map(ring => 
          ring.map(coord => ({ lat: coord[1], lng: coord[0] }))
        );
        allPaths.push(...paths);
      });
      
      // Skip invalid geometries
      if (!allPaths || allPaths.length === 0) {
        console.warn('Invalid MultiPolygon geometry for:', properties.code || 'unknown');
        return;
      }
      
      // Determine if this polygon has AGS data
      const hasAgsData = properties.ags_data && this.selectedAgsVariables.length > 0;
      const hasCrimeData = properties.crime_data && this.selectedCrimeTypes.length > 0;
      
      // Create single polygon with all paths
      const polygon = new google.maps.Polygon({
        paths: allPaths,
        strokeColor: this.getStrokeColor(properties),
        strokeOpacity: 0.8,
        strokeWeight: this.getStrokeWeight(properties),
        fillColor: this.getFillColor(properties),
        fillOpacity: this.getFillOpacity(properties),
        map: this.map,
        zIndex: this.getPolygonZIndex(properties),
        clickable: true,
        geodesic: false
      });
      
      // Store data type on polygon for reference
      polygon.dataType = hasAgsData ? 'ags' : (hasCrimeData ? 'crime' : 'base');
      
      // Store intensity level for AGS polygons (needed for scale toggle)
      if (hasAgsData) {
        const intensityLevel = properties.ags_risk_level || properties.ags_intensity_level;
        if (intensityLevel) {
          polygon.set('intensityLevel', intensityLevel);
        }
      }
      
      // Add click event for info display
      polygon.addListener('click', (event) => {
        this.showPolygonInfo(properties, event.latLng);
      });
      
      // Add hover highlight and popover events
      polygon.addListener('mouseover', (event) => {
        // Highlight the polygon on hover
        this.highlightPolygon(polygon, properties);
        
        // Show appropriate popover based on data type and zoom level
        // Enable hover for all layers (States at zoom 4-5, Counties at zoom 6-8, etc.)
        if (hasAgsData) {
          console.log('🖱️ Showing AGS hover for multi-polygon:', properties.code);
          this.displayAgsHoverPopover(properties, event);
        } else if (hasCrimeData && this.currentZoom >= 10) {
          // Crime data only available at zoom 10+ (Tracts, Block Groups, Blocks)
          this.displayHoverPopover(properties, event);
        }
      });
      
      polygon.addListener('mouseout', () => {
        // Remove highlight
        this.unhighlightPolygon(polygon, properties);
        
        // Hide popovers
        this.hideHoverPopover();
        this.hideAgsHoverPopover();
      });
      
      // Store reference for cleanup in appropriate array
      if (hasAgsData) {
        this.agsPolygonLayers.push(polygon);
      } else {
        this.polygonLayers.push(polygon);
      }
    },

    createSinglePolygon(geometry, properties) {
      // Convert GeoJSON coordinates to Google Maps format
      const paths = this.convertGeoJsonToGoogleMaps(geometry);
      
      // Skip invalid geometries
      if (!paths || paths.length === 0) {
        console.warn('Invalid geometry paths for polygon:', properties.code || 'unknown');
        return;
      }
      
      // Determine if this polygon has AGS data
      const hasAgsData = properties.ags_data && this.selectedAgsVariables.length > 0;
      const hasCrimeData = properties.crime_data && this.selectedCrimeTypes.length > 0;
      
      console.log('🔧 Creating polygon:', {
        unitCode: properties.code,
        hasAgsData,
        hasCrimeData,
        agsData: properties.ags_data,
        intensityLevel: properties.ags_intensity_level
      });
      
      // Create polygon with improved styling to prevent overlaps
      const polygon = new google.maps.Polygon({
        paths: paths,
        strokeColor: this.getStrokeColor(properties),
        strokeOpacity: 0.8, // Increased opacity for better boundary visibility
        strokeWeight: this.getStrokeWeight(properties),
        fillColor: this.getFillColor(properties),
        fillOpacity: this.getFillOpacity(properties),
        map: this.map,
        zIndex: this.getPolygonZIndex(properties), // Add z-index for proper layering
        clickable: true,
        geodesic: false // Disable geodesic to prevent distortion
      });
      
      // Store data type on polygon for reference
      polygon.dataType = hasAgsData ? 'ags' : (hasCrimeData ? 'crime' : 'base');
      
      // Store intensity level for AGS polygons (needed for scale toggle)
      if (hasAgsData) {
        const intensityLevel = properties.ags_risk_level || properties.ags_intensity_level;
        if (intensityLevel) {
          polygon.set('intensityLevel', intensityLevel);
        }
      }
      
      // Add click event for info display
      polygon.addListener('click', (event) => {
        this.showPolygonInfo(properties, event.latLng);
      });
      
      // Add hover highlight and popover events
      polygon.addListener('mouseover', (event) => {
        // Highlight the polygon on hover
        this.highlightPolygon(polygon, properties);
        
        // Show appropriate popover based on data type and zoom level
        // Enable hover for all layers (States at zoom 4-5, Counties at zoom 6-8, etc.)
        if (hasAgsData) {
          console.log('🖱️ Showing AGS hover for:', properties.code);
          this.displayAgsHoverPopover(properties, event);
        } else if (hasCrimeData && this.currentZoom >= 10) {
          // Crime data only available at zoom 10+ (Tracts, Block Groups, Blocks)
          this.displayHoverPopover(properties, event);
        }
      });
      
      polygon.addListener('mouseout', () => {
        // Remove highlight
        this.unhighlightPolygon(polygon, properties);
        
        // Hide popovers
        this.hideHoverPopover();
        this.hideAgsHoverPopover();
      });
      
      // Store reference for cleanup in appropriate array
      if (hasAgsData) {
        this.agsPolygonLayers.push(polygon);
      } else {
        this.polygonLayers.push(polygon);
      }
    },
    
    convertGeoJsonToGoogleMaps(geometry) {
      if (geometry.type === 'Polygon') {
        return geometry.coordinates.map(ring => 
          ring.map(coord => ({ lat: coord[1], lng: coord[0] }))
        );
      }
      return [];
    },
    
    getStrokeColor(properties) {
      // Use consistent dark blue stroke color for all layers
      return '#000000'; // Dark blue for all layers
    },

    getStrokeWeight(properties) {
      // Use consistent stroke weight to prevent overlapping appearance
      const layerWeights = {
        'States': 1.2,
        'Counties': 1.0,
        'ZIP Codes': 0.8,
        'Census Tracts': 0.6,
        'Block Groups': 0.4, // Thinner for block groups to reduce overlap appearance
        'Blocks': 0.3
      };
      
      let baseWeight = layerWeights[this.activeLayer] || 0.5;
      
      // If crime data is enabled, slightly adjust weight but keep it minimal
      if (this.selectedCrimeTypes.length > 0 && properties.crime_data) {
        const crimeData = properties.crime_data;
        
        // Calculate average of selected crime types
        let totalValue = 0;
        let validCount = 0;
        
        this.selectedCrimeTypes.forEach(crimeType => {
          const value = crimeData[crimeType];
          if (value !== undefined && value !== null) {
            totalValue += parseFloat(value);
            validCount++;
          }
        });
        
        if (validCount > 0) {
          const averageValue = totalValue / validCount;
          const normalizedValue = Math.min(Math.max(averageValue / 200, 0), 1);
          
          // Only slight weight increase for high crime areas
          return baseWeight + (normalizedValue * 0.2);
        }
      }
      
      return baseWeight;
    },
    
    getPolygonZIndex(properties) {
      // Set z-index based on layer type to ensure proper layering
      const layerZIndex = {
        'States': 1,
        'Counties': 2,
        'ZIP Codes': 3,
        'Census Tracts': 4,
        'Block Groups': 5,
        'Blocks': 6
      };
      
      let baseZIndex = layerZIndex[this.activeLayer] || 1;
      
      // Add small variation based on crime data to bring high-crime areas to front
      if (this.selectedCrimeTypes.length > 0 && properties.crime_data) {
        const crimeData = properties.crime_data;
        let totalValue = 0;
        let validCount = 0;
        
        this.selectedCrimeTypes.forEach(crimeType => {
          const value = crimeData[crimeType];
          if (value !== undefined && value !== null) {
            totalValue += parseFloat(value);
            validCount++;
          }
        });
        
        if (validCount > 0) {
          const averageValue = totalValue / validCount;
          const normalizedValue = Math.min(Math.max(averageValue / 200, 0), 1);
          
          // Add up to 100 to z-index for high crime areas
          return baseZIndex + Math.floor(normalizedValue * 100);
        }
      }
      
      return baseZIndex;
    },
    
    getFillColor(properties) {
      // Priority 1: AGS data coloring (if AGS variables are selected and data is available)
      if (this.selectedAgsVariables.length > 0 && properties.ags_data) {
        console.log('🎨 Using AGS color for feature:', {
          unitCode: properties.code,
          agsData: properties.ags_data,
          intensityLevel: properties.ags_intensity_level
        });
        return this.getAgsDataColor(properties);
      }
      
      // Priority 2: Risk level coloring (if available from backend)
      if (properties.risk_level && properties.risk_level !== 'no_data') {
        return this.getFeatureColor(properties);
      }
      
      // Priority 3: If crime types are selected but no crime data available, show neutral color
      if (this.selectedCrimeTypes.length > 0) {
        return '#F3F4F6'; // Light gray to indicate no crime data available
      }
      
      // Default: Use light cream color for all layers when no data
      return '#FFFBEB'; // Light cream/yellow for all layers
    },

    /**
     * Get color based on AGS intensity level (mirrors crime risk level coloring)
     * Uses a teal color scheme to differentiate from crime data
     * 
     * @param {Object} properties - Feature properties containing ags_intensity_level
     * @returns {String} Hex color code
     */
    getAgsDataColor(properties) {
      console.log('🎨 Getting AGS data color for:', {
        unitCode: properties.code,
        intensityLevel: properties.ags_intensity_level,
        riskLevel: properties.ags_risk_level,
        agsData: properties.ags_data
      });
      
      // Priority 1: Use AGS risk level if available (from state/county comparison)
      if (properties.ags_risk_level && properties.ags_risk_level !== 'no_data') {
        const color = this.agsStore.getAgsIntensityColor(properties.ags_risk_level);
        console.log('🎨 Using risk level color (comparison mode):', {
          riskLevel: properties.ags_risk_level,
          color: color
        });
        return color;
      }
      
      // Priority 2: Use AGS intensity level if available (viewport mode)
      if (properties.ags_intensity_level && properties.ags_intensity_level !== 'no_data') {
        const color = this.agsStore.getAgsIntensityColor(properties.ags_intensity_level);
        console.log('🎨 Using intensity level color (viewport mode):', {
          intensityLevel: properties.ags_intensity_level,
          color: color
        });
        return color;
      }
      
      // Priority 2: Fallback to raw value calculation (for backward compatibility)
      if (properties.ags_data && this.selectedAgsVariables.length > 0) {
        // Try to extract a numeric value from the first category
        const firstCategory = Object.keys(properties.ags_data)[0];
        if (firstCategory && properties.ags_data[firstCategory]) {
          const categoryData = properties.ags_data[firstCategory];
          let variableValue = null;
          
          // Try to get primary_value first, then raw numeric value
          if (typeof categoryData === 'object' && categoryData.primary_value !== undefined) {
            variableValue = categoryData.primary_value;
          } else if (typeof categoryData === 'number') {
            variableValue = categoryData;
          }
          
          if (variableValue !== null && variableValue !== undefined && typeof variableValue === 'number') {
            // Simple normalization for fallback
            const normalizedValue = Math.min(Math.max(variableValue / 100000, 0), 1); // Adjust max based on data type
            const color = this.getAgsColorByNormalizedValue(normalizedValue);
            console.log('🎨 Using fallback color calculation:', {
              variableValue,
              normalizedValue,
              color
            });
            return color;
          }
        }
      }
      
      // Default: Light gray for no data
      console.log('🎨 Using default AGS color (no data)');
      return '#f3f4f6';
    },



    /**
     * Get color by normalized value (0-1 scale)
     * 
     * @param {Number} normalizedValue - Value between 0 and 1
     * @returns {String} Hex color code
     */
    getAgsColorByNormalizedValue(normalizedValue) {
      const agsColorScale = [
        '#059669', // Dark green (0-14%)
        '#22c55e', // Green (14-28%)
        '#84cc16', // Yellow-green (28-42%)
        '#eab308', // Yellow (42-57%)
        '#f97316', // Orange (57-71%)
        '#ef4444', // Red (71-85%)
        '#dc2626'  // Dark red (85-100%)
      ];
      
      const colorIndex = Math.min(Math.floor(normalizedValue * agsColorScale.length), agsColorScale.length - 1);
      return agsColorScale[colorIndex];
    },

    /**
     * Get opacity for AGS data polygons
     * Uses the AGS data opacity setting from the store
     * 
     * @param {Object} properties - Feature properties containing ags_data
     * @returns {Number} Opacity value between 0 and 1
     */
    getBaseAgsOpacityForLayer() {
      // Base opacity for different layers - increased for better visibility
      const layerOpacities = {
        'States': 0.7,          // Medium-high for large areas
        'Counties': 0.75,       // Medium-high for large areas
        'ZIP Codes': 0.8,       // High
        'Census Tracts': 0.85,  // High
        'Block Groups': 0.9,    // Very high
        'Blocks': 1.0           // Full opacity for blocks as they're smallest
      };
      return layerOpacities[this.activeLayer] || 0.8;
    },

    getAgsDataOpacity(properties) {
      if (!properties.ags_data || this.selectedAgsVariables.length === 0) {
        return 0.1; // Very light for no data
      }
      
      // Apply user-controlled AGS opacity multiplier
      return this.getBaseAgsOpacityForLayer() * this.agsDataOpacity;
    },

    /**
     * Get feature color based on risk level
     * Maps risk levels to 7-level color scheme for better granularity
     * 
     * @param {Object} properties - Feature properties containing risk_level
     * @returns {String} Hex color code
     */
    getFeatureColor(properties) {
      const riskLevel = properties.risk_level;
      
      // Define 7-level color scheme for risk levels
      const colorMap = {
        'very_low': '#059669',      // Dark green (0-14%)
        'low': '#22c55e',           // Green (14-28%)
        'low_moderate': '#84cc16',  // Yellow-green (28-42%)
        'moderate': '#eab308',      // Yellow (42-57%)
        'moderate_high': '#f97316', // Orange (57-71%)
        'high': '#ef4444',          // Red (71-85%)
        'very_high': '#dc2626',     // Dark red (85-100%)
        'no_data': '#9ca3af'        // Gray
      };
      
      // Return mapped color or default to gray for unknown risk levels
      return colorMap[riskLevel] || colorMap['no_data'];
    },

    getDynamicCrimeColor(properties) {
      const crimeData = properties.crime_data;
      
      // Calculate average of selected crime types
      let totalValue = 0;
      let validCount = 0;
      
      this.selectedCrimeTypes.forEach(crimeType => {
        const value = crimeData[crimeType];
        if (value !== undefined && value !== null) {
          totalValue += parseFloat(value);
          validCount++;
        }
      });
      
      if (validCount === 0) {
        return this.getBaseFillColor();
      }
      
      const averageValue = totalValue / validCount;
      
      // Use discrete risk-based colors instead of gradient
      return this.getCrimeColorByValueDiscrete(averageValue);
    },

    /**
     * Get discrete risk-based color for viewport mode
     * Uses 7-level color scheme for better granularity
     */
    getCrimeColorByValueDiscrete(crimeValue) {
      // Calculate normalized value using current viewport range
      const range = this.currentCrimeRange.max - this.currentCrimeRange.min;
      const normalizedValue = range > 0 
        ? Math.min(Math.max((crimeValue - this.currentCrimeRange.min) / range, 0), 1)
        : 0;
      
      // Convert normalized value (0-1) to percentile (0-100)
      const percentile = normalizedValue * 100;
      
      // Use 7-level thresholds for better differentiation
      // Very Low: 0-14th percentile
      // Low: 14-28th percentile
      // Low-Moderate: 28-42nd percentile
      // Moderate: 42-57th percentile
      // Moderate-High: 57-71st percentile
      // High: 71-85th percentile
      // Very High: 85-100th percentile
      if (percentile < 14) {
        return '#059669'; // Dark green - Very Low
      } else if (percentile < 28) {
        return '#22c55e'; // Green - Low
      } else if (percentile < 42) {
        return '#84cc16'; // Yellow-green - Low-Moderate
      } else if (percentile < 57) {
        return '#eab308'; // Yellow - Moderate
      } else if (percentile < 71) {
        return '#f97316'; // Orange - Moderate-High
      } else if (percentile < 85) {
        return '#ef4444'; // Red - High
      } else {
        return '#dc2626'; // Dark red - Very High
      }
    },

    getCrimeColorByValue(crimeValue) {
      // Use current dynamic range for normalization
      const range = this.currentCrimeRange.max - this.currentCrimeRange.min;
      const normalizedValue = range > 0 
        ? Math.min(Math.max((crimeValue - this.currentCrimeRange.min) / range, 0), 1)
        : 0;
      
      // Debug logging (remove in production)
      if (import.meta.env.DEV) {
        console.log('Crime Color Calculation:', {
          crimeValue,
          range: this.currentCrimeRange,
          normalizedValue,
          rangeSize: range
        });
      }
      
      // Get the base color for the current layer
      const baseColor = this.getBaseFillColor();
      
      // Define color gradient based on the current layer's base color
      const colorStops = this.getLayerSpecificColorStops(baseColor);
      
      // Find the appropriate color based on normalized value
      for (let i = 0; i < colorStops.length - 1; i++) {
        const current = colorStops[i];
        const next = colorStops[i + 1];
        
        if (normalizedValue >= current.threshold && normalizedValue <= next.threshold) {
          // Interpolate between colors for smooth gradient
          const ratio = (normalizedValue - current.threshold) / (next.threshold - current.threshold);
          return this.interpolateColor(current.color, next.color, ratio);
        }
      }
      
      // Return the darkest color for highest values
      return colorStops[colorStops.length - 1].color;
    },

    getLayerSpecificColorStops(baseColor) {
      // Use AGS-style color gradient: light yellow/cream → orange → dark red
      return [
        { threshold: 0.0, color: '#FFFBEB' },   // Very light cream/yellow (lowest crime)
        { threshold: 0.1, color: '#FEF3C7' },   // Light yellow
        { threshold: 0.25, color: '#FDE68A' },  // Yellow
        { threshold: 0.4, color: '#FBBF24' },   // Golden yellow
        { threshold: 0.55, color: '#F59E0B' },  // Orange
        { threshold: 0.7, color: '#EA580C' },   // Dark orange
        { threshold: 0.85, color: '#DC2626' },  // Red
        { threshold: 1.0, color: '#991B1B' }    // Dark red (highest crime)
      ];
    },

    interpolateColor(color1, color2, ratio) {
      // Convert hex colors to RGB
      const rgb1 = this.hexToRgb(color1);
      const rgb2 = this.hexToRgb(color2);
      
      if (!rgb1 || !rgb2) {
        return color1; // Fallback to first color if conversion fails
      }
      
      // Interpolate each RGB component
      const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * ratio);
      const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * ratio);
      const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * ratio);
      
      // Convert back to hex
      return this.rgbToHex(r, g, b);
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    },

    rgbToHex(r, g, b) {
      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    },

    getBaseFillColor() {
      // Use AGS-style light cream/yellow as base color for all layers
      return '#FFFBEB';
    },
    
    getFillOpacity(properties) {
      // Priority 1: AGS data opacity (if AGS variables are selected and data is available)
      if (this.selectedAgsVariables.length > 0 && properties.ags_data) {
        return this.getAgsDataOpacity(properties);
      }
      
      // Priority 2: Crime data opacity
      if (this.selectedCrimeTypes.length === 0 || !properties.crime_data) {
        // Use consistent base opacity for all layers (no crime data)
        const layerOpacities = {
          'States': 0.1,          // Very light for large areas
          'Counties': 0.12,       // Very light for large areas
          'ZIP Codes': 0.15,      // Light
          'Census Tracts': 0.2,   // Light
          'Block Groups': 0.15,   // Light
          'Blocks': 0.25          // Slightly higher for blocks as they're smaller
        };
        
        return layerOpacities[this.activeLayer] || 0.15;
      }
      
      // When using dynamic crime colors, apply user-controlled opacity
      const baseOpacity = this.getBaseOpacityForLayer();
      return baseOpacity * this.crimeDataOpacity;
    },
    
    getCrimeOpacity(crimeValue) {
      // Opacity scale based on crime value with moderate differences to keep map labels visible
      // Assuming crime values are indices from 0-200
      const normalizedValue = Math.min(Math.max(crimeValue / 200, 0), 1);
      
      // Use lighter opacity range to ensure Google Maps labels remain visible
      const minOpacity = 0.15; // Very light for low crime
      const maxOpacity = 0.7;  // Moderate for high crime (reduced from 0.9)
      
      // Apply exponential scaling for more dramatic differences
      const exponentialValue = Math.pow(normalizedValue, 0.5); // More aggressive scaling
      
      return minOpacity + (exponentialValue * (maxOpacity - minOpacity));
    },
    
    showPolygonInfo(properties, position) {
      // Close any existing Google Maps info window
      if (this.infoWindow) {
        this.infoWindow.close();
        this.infoWindow = null;
      }
      
      // Extract geographic information from properties
      const unitCode = properties.code || properties.bg22_code || properties.bl_code || properties.tr22_code || properties.zcta5_code || properties.state_code || '';
      const unitType = this.determineUnitType(properties);
      const unitName = properties.name || '';
      
      // Extract hierarchical codes
      const stateCode = properties.state || this.extractStateFromCode(unitCode);
      const countyCode = properties.county || this.extractCountyFromCode(unitCode);
      const tractCode = properties.tract || this.extractTractFromCode(unitCode);
      const blockGroupCode = properties.block_group || this.extractBlockGroupFromCode(unitCode);
      
      // Calculate crime intensity if crime data is available
      let combinedValue = 0;
      let intensityPercent = 0;
      
      if (properties.crime_data && this.selectedCrimeTypes.length > 0) {
        const crimeData = properties.crime_data;
        let totalValue = 0;
        let validCount = 0;
        
        this.selectedCrimeTypes.forEach(crimeType => {
          const value = crimeData[crimeType];
          if (value !== undefined && value !== null) {
            totalValue += parseFloat(value);
            validCount++;
          }
        });
        
        if (validCount > 0) {
          combinedValue = totalValue / validCount;
          
          // Use the same dynamic range calculation as the color system
          if (this.currentCrimeRange && this.currentCrimeRange.max > this.currentCrimeRange.min) {
            const range = this.currentCrimeRange.max - this.currentCrimeRange.min;
            const normalizedValue = Math.min(Math.max((combinedValue - this.currentCrimeRange.min) / range, 0), 1);
            intensityPercent = Math.round(normalizedValue * 100);
            
            // Debug logging for popup calculation
            if (import.meta.env.DEV) {
              console.log('Popup Intensity Calculation:', {
                combinedValue,
                currentCrimeRange: this.currentCrimeRange,
                range,
                normalizedValue,
                intensityPercent,
                selectedCrimeTypes: this.selectedCrimeTypes,
                crimeData
              });
            }
          } else {
            // Fallback to fixed range if dynamic range not available
            intensityPercent = Math.min(Math.round((combinedValue / 200) * 100), 100);
            
            if (import.meta.env.DEV) {
              console.log('Popup Fallback Calculation:', {
                combinedValue,
                intensityPercent,
                reason: 'No dynamic range available'
              });
            }
          }
        }
      }
      
      // Get detailed crime intensity information
      const crimeIntensityDetails = this.getCrimeIntensityDetails(properties);
      
      // Extract risk comparison data
      const riskLevel = properties.risk_level || null;
      const normalizedScore = properties.normalized_score !== undefined ? properties.normalized_score : null;
      const crimeIndex = properties.crime_index !== undefined ? properties.crime_index : null;
      const riskPercentile = properties.risk_percentile !== undefined ? properties.risk_percentile : null;
      const perCapitaRate = properties.per_capita_rate !== undefined ? properties.per_capita_rate : null;
      const comparisonReference = properties.comparison_reference || null;

      // Update popup data
      this.popupData = {
        unitCode: unitCode,
        unitType: unitType,
        unitName: unitName,
        stateCode: stateCode,
        countyCode: countyCode,
        tractCode: tractCode,
        blockGroupCode: blockGroupCode,
        populationData: properties.population_cy || properties.population || null,
        crimeData: properties.crime_data || {},
        agsData: properties.ags_data || {},
        agsIntensityLevel: properties.ags_intensity_level || null,
        agsRiskLevel: properties.ags_risk_level || null,
        agsValue: properties.ags_value || null,
        agsPercentage: properties.ags_percentage || null,
        agsComparisonReference: properties.ags_comparison_reference || null,
        combinedValue: combinedValue,
        intensityPercent: intensityPercent,
        // Add detailed crime intensity information for consistency checking
        crimeIntensityDetails: crimeIntensityDetails,
        polygonColor: this.getPolygonColor(properties),
        // Add risk comparison data
        riskLevel: riskLevel,
        normalizedScore: normalizedScore,
        crimeIndex: crimeIndex,
        riskPercentile: riskPercentile,
        perCapitaRate: perCapitaRate,
        comparisonReference: comparisonReference
      };
      
      // Determine which popup to show based on available data
      const hasCrimeData = properties.crime_data && Object.keys(properties.crime_data).length > 0;
      const hasAgsData = properties.ags_data && Object.keys(properties.ags_data).length > 0;
      
      if (hasAgsData && this.selectedAgsVariables.length > 0) {
        // Show AGS popup if AGS data is available and variables are selected
        this.showAgsPopup = true;
        this.showPopup = false;
      } else if (hasCrimeData && this.selectedCrimeTypes.length > 0) {
        // Show crime popup if crime data is available and crime types are selected
        this.showPopup = true;
        this.showAgsPopup = false;
      } else {
        // Default to crime popup for backward compatibility
        this.showPopup = true;
        this.showAgsPopup = false;
      }
    },
    
    determineUnitType(properties) {
      // Determine unit type based on available properties
      if (properties.bl_code || (properties.code && properties.code.length >= 15)) {
        return 'block';
      } else if (properties.bg22_code || (properties.code && properties.code.length >= 12)) {
        return 'block_group';
      } else if (properties.tr22_code) {
        return 'tracts';
      } else if (properties.zcta5_code) {
        return 'zips';
      } else if (properties.state_code) {
        return 'states';
      } else if (properties.county_code) {
        // Counties not available, fallback to states
        return 'states';
      }
      
      // Default based on current active layer
      if (this.activeLayer === 'Blocks') return 'block';
      if (this.activeLayer === 'Block Groups') return 'block_group';
      if (this.activeLayer === 'Census Tracts') return 'tracts';
      if (this.activeLayer === 'ZIP Codes') return 'zips';
      if (this.activeLayer === 'Counties (Not Available)') return 'states'; // Fallback to states
      if (this.activeLayer === 'States') return 'states';
      
      return 'block_group'; // Default fallback
    },
    
    extractStateFromCode(code) {
      if (code && code.length >= 2) {
        return code.substring(0, 2);
      }
      return null;
    },
    
    extractCountyFromCode(code) {
      if (code && code.length >= 5) {
        return code.substring(2, 5);
      }
      return null;
    },
    
    extractTractFromCode(code) {
      if (code && code.length >= 11) {
        return code.substring(0, 11);
      }
      return null;
    },
    
    extractBlockGroupFromCode(code) {
      if (code && code.length >= 12) {
        return code.substring(0, 12);
      }
      return null;
    },
    
    closePopup() {
      this.showPopup = false;
      this.popupData = {
        unitCode: '',
        unitType: '',
        unitName: '',
        stateCode: '',
        countyCode: '',
        tractCode: '',
        blockGroupCode: '',
        populationData: null,
        crimeData: {},
        agsData: {},
        agsIntensityLevel: null,
        agsRiskLevel: null,
        agsValue: null,
        agsComparisonReference: null,
        combinedValue: 0,
        intensityPercent: 0
      };
    },

    closeAgsPopup() {
      this.showAgsPopup = false;
      this.popupData = {
        unitCode: '',
        unitType: '',
        unitName: '',
        stateCode: '',
        countyCode: '',
        tractCode: '',
        blockGroupCode: '',
        populationData: null,
        crimeData: {},
        agsData: {},
        agsIntensityLevel: null,
        agsRiskLevel: null,
        agsValue: null,
        agsComparisonReference: null,
        combinedValue: 0,
        intensityPercent: 0
      };
    },

    calculateDynamicCrimeRange(geoJsonData) {
      if (!geoJsonData || !geoJsonData.features || this.selectedCrimeTypes.length === 0) {
        return { min: 0, max: 200 }; // Default range
      }

      let minValue = Infinity;
      let maxValue = -Infinity;
      let validValues = [];

      geoJsonData.features.forEach(feature => {
        const crimeData = feature.properties.crime_data;
        if (crimeData) {
          let totalValue = 0;
          let validCount = 0;

          this.selectedCrimeTypes.forEach(crimeType => {
            const value = crimeData[crimeType];
            if (value !== undefined && value !== null) {
              totalValue += parseFloat(value);
              validCount++;
            }
          });

          if (validCount > 0) {
            const averageValue = totalValue / validCount;
            validValues.push(averageValue);
            minValue = Math.min(minValue, averageValue);
            maxValue = Math.max(maxValue, averageValue);
          }
        }
      });

      // If no valid data found, return default range
      if (validValues.length === 0) {
        return { min: 0, max: 200 };
      }

      // Add some padding to the range for better visualization
      const range = maxValue - minValue;
      const padding = range * 0.1; // 10% padding
      
      const result = {
        min: Math.max(0, minValue - padding),
        max: maxValue + padding,
        actualMin: minValue,
        actualMax: maxValue,
        count: validValues.length
      };
      
      // Debug logging for range calculation
      if (import.meta.env.DEV) {
        console.log('Calculate Dynamic Crime Range:', {
          validValues: validValues.slice(0, 10), // Show first 10 values
          minValue,
          maxValue,
          range,
          padding,
          result,
          selectedCrimeTypes: this.selectedCrimeTypes
        });
      }
      
      return result;
    },

    updateDynamicCrimeScale(geoJsonData) {
      const range = this.calculateDynamicCrimeRange(geoJsonData);
      
      // Store current range for use in color calculations
      this.currentCrimeRange = range;
      
      // Debug logging for range calculation
      if (import.meta.env.DEV) {
        console.log('Dynamic Crime Range Updated:', {
          range,
          selectedCrimeTypes: this.selectedCrimeTypes,
          featureCount: geoJsonData?.features?.length || 0
        });
      }
      
      if (range.count === 0) {
        // No data available, hide the scale and legend
        this.crimeColorScale = [];
        this.showCrimeLegend = false;
        return;
      }

      // Create dynamic scale based on actual data range
      const steps = 5; // Reduced to 5 for cleaner legend
      const stepSize = (range.max - range.min) / (steps - 1);
      
      this.crimeColorScale = [];
      
      // Define risk level labels
      const riskLabels = ['Very Low', 'Low', 'Moderate', 'High', 'Very High'];
      
      for (let i = 0; i < steps; i++) {
        const value = range.min + (stepSize * i);
        const normalizedValue = i / (steps - 1); // 0 to 1
        
        // Get color for this normalized value
        const color = this.getCrimeColorByNormalizedValue(normalizedValue);
        
        // Create risk-based label with value range
        let label;
        if (i === 0) {
          label = `${riskLabels[i]} (${Math.round(value)})`;
        } else if (i === steps - 1) {
          label = `${riskLabels[i]} (${Math.round(value)}+)`;
        } else {
          const nextValue = range.min + (stepSize * (i + 1));
          label = `${riskLabels[i]} (${Math.round(value)}-${Math.round(nextValue)})`;
        }
        
        this.crimeColorScale.push({
          color: color,
          label: label,
          value: value,
          riskLevel: riskLabels[i]
        });
      }
      
      // Auto-show legend when crime data is first loaded
      if (this.crimeColorScale.length > 0 && this.currentZoom >= 10) {
        this.showCrimeLegend = true;
      }
    },

    getCrimeColorByNormalizedValue(normalizedValue) {
      // Use layer-specific color gradient based on current active layer
      const colorStops = this.getLayerSpecificColorStops();
      
      // Find the appropriate color based on normalized value
      for (let i = 0; i < colorStops.length - 1; i++) {
        const current = colorStops[i];
        const next = colorStops[i + 1];
        
        if (normalizedValue >= current.threshold && normalizedValue <= next.threshold) {
          // Interpolate between colors for smooth gradient
          const ratio = (normalizedValue - current.threshold) / (next.threshold - current.threshold);
          return this.interpolateColor(current.color, next.color, ratio);
        }
      }
      
      // Return the darkest color for highest values
      return colorStops[colorStops.length - 1].color;
    },

    /**
     * Show hover popover with crime data and risk information
     */
    displayHoverPopover(properties, event) {
      // Clear any existing timeout
      this.clearHoverTimeout();
      
      // Only show if conditions are met
      if (this.currentZoom < 10 || this.selectedCrimeTypes.length === 0) {
        return;
      }
      
      // Get mouse position relative to the map container
      const mapContainer = document.getElementById('cresiteiq2-map');
      const rect = mapContainer.getBoundingClientRect();
      
      // Calculate position with offset using constants (no hardcoded values)
      // Position popover to the right and slightly above the cursor
      let x = event.domEvent.clientX - rect.left + this.hoverPopupOffsetX;
      let y = event.domEvent.clientY - rect.top - this.hoverPopupOffsetY;
      
      // Ensure popover doesn't go off screen (viewport boundary checks)
      const popoverWidth = 250; // Estimated popover width
      
      if (x + popoverWidth > rect.width) {
        x = event.domEvent.clientX - rect.left - popoverWidth - this.hoverPopupOffsetX; // Position to the left
      }
      
      if (y < 0) {
        y = event.domEvent.clientY - rect.top + this.hoverPopupOffsetY; // Position below cursor
      }
      
      // Extract unit information
      const unitCode = properties.code || properties.bg22_code || properties.bl_code || '';
      const unitType = this.determineUnitType(properties);
      
      // Extract risk information from properties
      const riskLevel = properties.risk_level || null;
      const riskPercentile = properties.risk_percentile !== undefined ? properties.risk_percentile : null;
      const perCapitaRate = properties.per_capita_rate !== undefined ? properties.per_capita_rate : null;
      const comparisonReference = properties.comparison_reference || null;
      
           
      // Set hover data with a small delay to prevent flickering
      this.hoverTimeout = setTimeout(() => {
        this.hoverPopoverData = {
          unitCode: unitCode,
          unitType: unitType,
          crimeData: properties.crime_data || {},
          riskLevel: riskLevel,
          riskPercentile: riskPercentile,
          perCapitaRate: perCapitaRate,
          comparisonReference: comparisonReference,
          position: { x, y }
        };
        this.showHoverPopover = true;
      }, 150);
    },

    /**
     * Hide hover popover
     */
    hideHoverPopover() {
      this.clearHoverTimeout();
      this.showHoverPopover = false;
    },

    /**
     * Clear hover timeout
     */
    clearHoverTimeout() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    },

    /**
     * Get crime type label from value
     */
    getCrimeTypeLabel(crimeType) {
      const crimeTypeObj = this.availableCrimeTypes.find(type => type.value === crimeType);
      return crimeTypeObj ? crimeTypeObj.label : crimeType;
    },

    getUnitTypeDisplayName(unitType) {
      const displayNames = {
        'states': 'State',
        'counties': 'County', 
        'zips': 'ZIP Code',
        'tracts': 'Tract',
        'block_group': 'Block Group',
        'block': 'Block'
      };
      return displayNames[unitType] || 'Geographic Unit';
    },

    /**
     * Highlight polygon on hover
     */
    highlightPolygon(polygon, properties) {
      // Store original styles for restoration
      if (!polygon.originalStyles) {
        polygon.originalStyles = {
          strokeColor: polygon.get('strokeColor'),
          strokeOpacity: polygon.get('strokeOpacity'),
          strokeWeight: polygon.get('strokeWeight'),
          fillOpacity: polygon.get('fillOpacity'),
          zIndex: polygon.get('zIndex')
        };
      }

      // Apply highlight styles
      polygon.setOptions({
        strokeColor: '#2563EB', // Bright blue highlight
        strokeOpacity: 1.0,
        strokeWeight: Math.max(polygon.originalStyles.strokeWeight + 1, 2),
        fillOpacity: Math.min(polygon.originalStyles.fillOpacity + 0.2, 0.8),
        zIndex: polygon.originalStyles.zIndex + 1000 // Bring to front
      });
    },

    /**
     * Remove highlight from polygon
     */
    unhighlightPolygon(polygon, properties) {
      // Restore original styles
      if (polygon.originalStyles) {
        polygon.setOptions({
          strokeColor: polygon.originalStyles.strokeColor,
          strokeOpacity: polygon.originalStyles.strokeOpacity,
          strokeWeight: polygon.originalStyles.strokeWeight,
          fillOpacity: polygon.originalStyles.fillOpacity,
          zIndex: polygon.originalStyles.zIndex
        });
      }
    },

    /**
     * Get the actual color being used for a polygon (for debugging/consistency)
     * This should always match getFillColor to ensure debug panel shows correct color
     */
    getPolygonColor(properties) {
      return this.getFillColor(properties);
    },

    /**
     * Get crime intensity details for a polygon
     */
    getCrimeIntensityDetails(properties) {
      if (!properties.crime_data || this.selectedCrimeTypes.length === 0) {
        return null;
      }

      const crimeData = properties.crime_data;
      let totalValue = 0;
      let validCount = 0;
      
      this.selectedCrimeTypes.forEach(crimeType => {
        const value = crimeData[crimeType];
        if (value !== undefined && value !== null) {
          totalValue += parseFloat(value);
          validCount++;
        }
      });
      
      if (validCount === 0) {
        return null;
      }

      const averageValue = totalValue / validCount;
      
      // Use comparison_reference if available, otherwise fall back to currentCrimeRange
      const comparisonRef = properties.comparison_reference;
      let rangeData = this.currentCrimeRange;
      
      // Backend sends min_index/max_index (not min_rate/max_rate)
      if (comparisonRef && comparisonRef.min_index !== undefined && comparisonRef.max_index !== undefined) {
        rangeData = {
          min: comparisonRef.min_index,
          max: comparisonRef.max_index,
          mean: comparisonRef.mean_index,
          median: comparisonRef.median_index,
          count: comparisonRef.total_entities,
          type: comparisonRef.type,
          code: comparisonRef.code
        };
      }
      
      // Calculate normalized value using the same method as colors
      let normalizedValue = 0;
      if (rangeData && rangeData.max > rangeData.min) {
        const range = rangeData.max - rangeData.min;
        normalizedValue = Math.min(Math.max((averageValue - rangeData.min) / range, 0), 1);
      } else {
        normalizedValue = Math.min(Math.max(averageValue / 200, 0), 1);
      }

      return {
        averageValue: averageValue,
        normalizedValue: normalizedValue,
        intensityPercent: Math.round(normalizedValue * 100),
        color: this.getCrimeColorByValue(averageValue),
        range: rangeData
      };
    },

    // Location search event handler
    onLocationSelected(locationData) {
      const { coordinates, name } = locationData;
      
      // Center map on the selected location and zoom to level 12
      this.map.setCenter(coordinates);
      this.map.setZoom(12);

      // Add a temporary marker to show the searched location
      this.addTemporaryLocationMarker(coordinates, name);

      // Show success message
      if (this.$toast) {
        this.$toast.success(`Zoomed to ${name}`);
      }
    },

    // Reset map to initial home view
    resetToHome() {
      if (this.map) {
        this.map.setCenter(this.initialMapSettings.center);
        this.map.setZoom(this.initialMapSettings.zoom);
        
        // Remove any temporary markers
        if (this.temporaryMarker) {
          this.temporaryMarker.setMap(null);
          this.temporaryMarker = null;
        }
        
        // Hide crime legend when resetting
        this.showCrimeLegend = false;
        // Show success message
        if (this.$toast) {
          this.$toast.success('Returned to home view');
        }
      }
    },

    // Zoom to user's current location
    zoomToCurrentLocation() {
      if (!navigator.geolocation) {
        if (this.$toast) {
          this.$toast.error('Geolocation is not supported by this browser');
        }
        return;
      }

      this.gettingLocation = true;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          // Center map on user's location and zoom to level 12
          this.map.setCenter(userLocation);
          this.map.setZoom(12);

          // Add a temporary marker to show current location
          this.addTemporaryLocationMarker(userLocation, 'Your Location');

          // Show success message
          if (this.$toast) {
            this.$toast.success('Zoomed to your current location');
          }

          this.gettingLocation = false;
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }

          if (this.$toast) {
            this.$toast.error(errorMessage);
          }

          this.gettingLocation = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    },

    addTemporaryLocationMarker(position, title) {
      // Remove any existing temporary marker
      if (this.temporaryMarker) {
        this.temporaryMarker.setMap(null);
      }

      // Determine marker color based on title/type
      const isCurrentLocation = title === 'Your Location';
      const markerColor = isCurrentLocation ? '#3B82F6' : '#EF4444'; // Blue for current location, red for search results

      // Create a new temporary marker
      this.temporaryMarker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: title,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${markerColor}" stroke="#FFFFFF" stroke-width="0.5"/>
              ${isCurrentLocation ? '<circle cx="12" cy="9" r="1.5" fill="#FFFFFF"/>' : ''}
            </svg>
          `),
          scaledSize: new google.maps.Size(28, 28),
          anchor: new google.maps.Point(14, 28)
        },
        animation: google.maps.Animation.DROP
      });

      // Remove the marker after 8 seconds (longer for current location)
      const timeout = isCurrentLocation ? 8000 : 5000;
      setTimeout(() => {
        if (this.temporaryMarker) {
          this.temporaryMarker.setMap(null);
          this.temporaryMarker = null;
        }
      }, timeout);
    },

    /**
     * Retry loading map data after an error
     * Useful for transient network errors
     */
    retryLoadMapData() {
      console.log('Retrying map data load...');
      
      // Reset error state
      this.apiErrorCount = 0;
      this.lastApiError = null;
      this.comparisonFallbackActive = false;
      
      // Show info notification
      this.$toast.info('Retrying...', {
        timeout: 2000,
        position: 'bottom-right'
      });
      
      // Reload map data
      this.loadMapData();
    },

    /**
     * Format risk level for display (7-level system)
     * Converts risk level codes to human-readable labels
     * 
     * @param {String} riskLevel - Risk level code
     * @returns {String} Formatted risk level label
     */
    formatRiskLevel(riskLevel) {
      const labels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };
      
      return labels[riskLevel] || 'Unknown';
    },

    /**
     * Get CSS classes for risk level badge (7-level system)
     * Returns Tailwind classes for styling risk level badges
     * 
     * @param {String} riskLevel - Risk level code
     * @returns {String} CSS class string
     */
    getRiskLevelBadgeClass(riskLevel) {
      const classes = {
        'very_low': 'bg-emerald-100 text-emerald-900',
        'low': 'bg-green-100 text-green-800',
        'low_moderate': 'bg-lime-100 text-lime-800',
        'moderate': 'bg-yellow-100 text-yellow-800',
        'moderate_high': 'bg-orange-100 text-orange-800',
        'high': 'bg-red-100 text-red-800',
        'very_high': 'bg-red-200 text-red-900',
        'no_data': 'bg-gray-100 text-gray-800'
      };
      
      return classes[riskLevel] || 'bg-gray-100 text-gray-800';
    },

    /**
     * Hide the crime legend
     */
    hideCrimeLegend() {
      this.showCrimeLegend = false;
    },

    /**
     * Hide the AGS legend
     */
    hideAgsLegend() {
      this.showAgsLegend = false;
    }
  }
}
</script>

<style scoped>
.cresiteiq2-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.sidebar {
  min-width: 320px;
  max-width: 400px;
  box-shadow: 2px 0 4px 0 rgba(0, 0, 0, 0.1);
}

.sidebar-header {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.sidebar-content {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.toolbar {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.map-container {
  background-color: #f3f4f6;
}

/* Opacity Slider Styling */
.opacity-slider {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: linear-gradient(to right, #DBEAFE 0%, #3B82F6 100%);
  border-radius: 4px;
  outline: none;
  cursor: pointer;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3B82F6;
  border: 2px solid #ffffff;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.opacity-slider::-webkit-slider-thumb:hover {
  background: #2563EB;
  transform: scale(1.1);
}

.opacity-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3B82F6;
  border: 2px solid #ffffff;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.opacity-slider::-moz-range-thumb:hover {
  background: #2563EB;
  transform: scale(1.1);
}

.opacity-slider::-moz-range-track {
  height: 8px;
  background: linear-gradient(to right, #DBEAFE 0%, #3B82F6 100%);
  border-radius: 4px;
  border: none;
}

.layer-colors-section,
.crime-data-section,
.map-controls-section {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1.5rem;
}

.layer-colors-section:last-child,
.crime-data-section:last-child,
.map-controls-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

/* Custom checkbox styling */
input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

/* Info window styling */
:deep(.gm-style-iw) {
  max-width: 300px !important;
}

:deep(.polygon-info) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.4;
}

:deep(.polygon-info h3) {
  margin: 0 0 8px 0;
  color: #1f2937;
}

:deep(.polygon-info h4) {
  margin: 12px 0 4px 0;
  color: #374151;
}

:deep(.polygon-info p) {
  margin: 2px 0;
  color: #4b5563;
  font-size: 14px;
}

.zoom-layer-overlay {
  pointer-events: auto;
}

.zoom-vertical-control {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem;
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.6);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -12px rgba(15, 23, 42, 0.45);
}

.zoom-slider-wrapper {
  height: 160px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
}

.layer-colors-panel {
  pointer-events: auto;
  min-width: 220px;
}

.crime-scale-overlay {
  pointer-events: auto;
}

.crime-scale-trigger {
  pointer-events: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.zoom-slider-vertical {
  width: 160px;
  height: 4px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  transform: rotate(-90deg);
  transform-origin: center;
  --zoom-thumb-color: #3b82f6;
  --zoom-thumb-border: #1d4ed8;
  --zoom-track-active: #3b82f6;
  --zoom-track-inactive: #cbd5e1;
  --zoom-progress: 0%;
  background: linear-gradient(
    to right,
    var(--zoom-track-active) 0%,
    var(--zoom-track-active) var(--zoom-progress),
    var(--zoom-track-inactive) var(--zoom-progress),
    var(--zoom-track-inactive) 100%
  );
  border-radius: 9999px;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
}

/* Chrome/Safari specific fixes */
@supports (-webkit-appearance: none) {
  .zoom-slider-vertical {
    background: var(--zoom-track-inactive);
  }
}

/* Chrome-specific thumb centering fix */
@media screen and (-webkit-min-device-pixel-ratio:0) {
  .zoom-slider-vertical::-webkit-slider-thumb {
    margin-top: -7px;
    margin-left: 0;
  }
}

/* Firefox specific fixes */
@-moz-document url-prefix() {
  .zoom-slider-vertical {
    background: var(--zoom-track-inactive);
  }
}

.zoom-slider-vertical::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background-color: var(--zoom-thumb-color);
  border: 2px solid var(--zoom-thumb-border);
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.zoom-slider-vertical::-webkit-slider-runnable-track {
  height: 4px;
  width: 100%;
  background: transparent;
  border-radius: 9999px;
}

.zoom-slider-vertical::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background-color: var(--zoom-thumb-color);
  border: 2px solid var(--zoom-thumb-border);
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.zoom-slider-vertical::-moz-range-track {
  height: 4px;
  width: 100%;
  background: linear-gradient(
    to right,
    var(--zoom-track-active) 0%,
    var(--zoom-track-active) var(--zoom-progress),
    var(--zoom-track-inactive) var(--zoom-progress),
    var(--zoom-track-inactive) 100%
  );
  border: none;
  border-radius: 9999px;
}


/* Hover popover styling */
.hover-popover-content {
  animation: fadeInPopover 0.2s ease-in-out;
  backdrop-filter: blur(2px);
  background-color: rgba(255, 255, 255, 0.95);
}

@keyframes fadeInPopover {
  from {
    opacity: 0;
    transform: translateY(5px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsive design */
@media (max-width: 1024px) {
  .sidebar {
    min-width: 280px;
    max-width: 320px;
  }
}

@media (max-width: 768px) {
  .cresiteiq2-container {
    flex-direction: column;
  }
  
  .sidebar {
    min-width: 100%;
    max-width: 100%;
    height: 300px;
  }
  
  .main-content {
    flex: 1;
  }
}
</style>
